// src/chat-ui/AppChat.jsx
import React, { useState, useRef, useEffect, useMemo } from "react";
import { Sandpack } from "@codesandbox/sandpack-react";
import {
  MessageSquare,
  UserCircle,
  BarChart3,
  PieChart,
  PanelLeft,
  X,
  AlertTriangle,
  Sparkles,
  Lightbulb,
  FileText,
  CheckCircle2,
  Loader2,
  XCircle,
  BrainCircuit,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Download,
  Clock,
  Code2,
  ShieldCheck,
  LayoutTemplate,
} from "lucide-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { useToast } from "./components/ToastProvider";

import { defaultSettings } from "./utils/defaultSettings";

import UsagePanel from "./components/UsagePanel";
import UsageOverviewPanel from "./components/UsageOverviewPanel";
import ProfilePanel from "./components/ProfilePanel";
import FeedbackPanel from "./components/FeedbackPanel";
import ChatHistoryPanel from "./components/ChatHistoryPanel";
import IconButton from "./components/IconButton";
import ChatBubble from "./components/ChatBubble";
import CanvasCodeView from "./components/CanvasCodeView";
import ChatInputBar from "./components/ChatInputBar";
import NotifyPanel from "./components/NotifyPanel";
import SettingsPanel from "./components/SettingsPanel";
import ConfirmDeleteModal from "./components/ConfirmDeleteModal";

// Safe localStorage helper — gracefully handles sandboxed/private browser contexts
const lsGet = (key) => { try { return localStorage.getItem(key); } catch { return null; } };

const API_BASE = process.env.REACT_APP_API_BASE;
const STREAM_PAINT_INTERVAL_MS = Math.max(
  16,
  Number(process.env.REACT_APP_STREAM_PAINT_INTERVAL_MS || 24)
);
const STREAM_CHARS_PER_TICK = Math.max(
  8,
  Number(process.env.REACT_APP_STREAM_CHARS_PER_TICK || 18)
);
const MAX_ATTACHMENT_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_CLIENT_OCR_CONTEXT_CHARS = Math.max(
  2000,
  Number(process.env.REACT_APP_MAX_CLIENT_OCR_CONTEXT_CHARS || 10000)
);
// Neutral, backend-agnostic progress narration. Rotates every few seconds so the
// user sees the system is actively working (not frozen), without claiming a
// specific action (e.g. "searching the internet") that may not apply to every turn.
const LLM_WAIT_HINTS = [
  "กำลังทำความเข้าใจคำถามของคุณ...",
  "กำลังรวบรวมข้อมูลที่เกี่ยวข้อง...",
  "กำลังประมวลผลและวิเคราะห์ข้อมูล...",
  "กำลังตรวจสอบและเรียบเรียงคำตอบ...",
  "กำลังจัดรูปแบบคำตอบให้อ่านง่าย...",
];
const COMPOSER_TOOL_CANVAS = "canvas";
// Attachment types accepted for document/OCR processing (kept in sync with the
// backend allowlist and the doc-processor). Excel is parsed into Markdown tables.
const ALLOWED_ATTACHMENT_EXTENSIONS = [".pdf", ".docx", ".xlsx", ".xlsm", ".xls"];
const guessAttachmentMime = (name = "") => {
  const n = String(name || "").toLowerCase();
  if (n.endsWith(".pdf")) return "application/pdf";
  if (n.endsWith(".xlsx") || n.endsWith(".xlsm"))
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (n.endsWith(".xls")) return "application/vnd.ms-excel";
  return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
};
const DESKTOP_SIDEBAR_COLLAPSE_STORAGE_KEY = "aihub.desktopSidebarCollapsed";

// ─── Canvas inspiration prompts (mega prompts) ──────────────────────────────
// Shown as quick-pick chips when Canvas mode is active. Clicking one sends the
// full prompt straight to the LLM. Add more entries here to grow the library.
const CANVAS_INSPIRATIONS = [
  {
    id: "interactive-web-design",
    label: "ออกแบบเว็บไซต์ทีละขั้นตอน",
    description: "ผู้ช่วยพาเลือกสไตล์ สี ฟอนต์ ฟีเจอร์ แล้วเจนโค้ดให้",
    icon: LayoutTemplate,
    prompt: `คุณคือ "AI Web Design Architect" ระบบผู้ช่วยออกแบบและเขียนโค้ดเว็บไซต์อัจฉริยะ หน้าที่ของคุณคือการนำทางฉันสร้างเว็บไซต์ผ่านระบบโต้ตอบทีละขั้นตอน (Interactive Step-by-Step) ทั้งหมด 5 ขั้นตอน โดยคุณต้องแสดงตัวเลือก (Choices) ให้ฉันเลือกในแต่ละรอบ และห้ามข้ามขั้นตอนเด็ดขาด จนกว่าฉันจะพิมพ์เลือกคำตอบในรอบนั้นๆ

เงื่อนไขในการทำงานของคุณ:
- ในแต่ละขั้นตอน ให้แนะนำสั้นๆ กระชับ และให้ตัวเลือก A, B, C, D (หรือเปิดโอกาสให้ฉันพิมพ์ความต้องการเองได้)
- เมื่อฉันเลือกคำตอบแล้ว ให้คุณสรุปสิ่งทึ่เลือกสั้นๆ แล้วจึงเริ่มคำถามในขั้นตอนถัดไป

นี่คือแผนงานทั้ง 5 ขั้นตอนที่คุณต้องพาฉันทำ:

[Step 1: เลือกสไตล์ของเว็บไซต์ (Website Layout & Style)]
นำเสนอรูปแบบเลย์เอาต์ เช่น Minimalist, Corporate Look, Creative Portfolio, หรือ Modern Dashboard

[Step 2: เลือกโทนสี (Color Palette)]
นำเสนอชุดสีที่เข้ากับสไตล์ใน Step 1 เช่น Light Mode คลีนๆ, Dark Mode ดุดัน, Pastel อบอุ่น, หรือ Vibrant Neon

[Step 3: เลือกฟอนต์และสไตล์ตัวอักษร (Typography & Fonts)]
นำเสนอคู่ฟอนต์ภาษาไทย/อังกฤษที่เข้ากัน เช่น ฟอนต์ไม่มีหัวดูโมเดิร์น (เช่น Prompt/Inter) หรือฟอนต์มีหัวดูทางการ (เช่น Sarabun/Merriweather)

[Step 4: เลือกฟังก์ชันและโครงสร้างเนื้อหา (Features & Sections)]
ถามว่าต้องการให้มีส่วนไหนบนหน้าเว็บบ้าง เช่น Navbar + Hero Banner + Features Grid + Contact Form

[Step 5: สรุปและเจนโค้ด (Final Review & Code Generation)]
สรุปตัวเลือกทั้งหมดที่ฉันเลือกตั้งแต่ Step 1-4 เพื่อให้ฉันกดยืนยัน เมื่อฉันยืนยันแล้ว ให้คุณเขียนโค้ด HTML/CSS (รวมในไฟล์เดียว) ที่สวยงาม สมบูรณ์แบบ และใช้งานได้จริงออกมาให้ทันที

---

หากคุณเข้าใจคำสั่งนี้และพร้อมทำงานแล้ว ให้เริ่มทักทายฉันสั้นๆ และเปิดคำถามใน [Step 1] เพื่อให้ฉันเลือกสไตล์ได้เลยครับ!`,
  },
  {
    id: "dashboard-google-sheet",
    label: "สร้าง Dashboard จาก Google Sheet",
    description: "วางลิงก์ Sheet (เปิดสิทธิ์อ่าน) ระบบจะดึงข้อมูลมาทำแดชบอร์ดให้",
    icon: BarChart3,
    requiresSheet: true,
    prompt: `คุณคือ "AI Dashboard Architect" ผู้ช่วยออกแบบ Interactive Dashboard จากข้อมูล Google Sheet ระบบได้ดึงข้อมูลจาก Sheet มาให้คุณแล้ว (ดูส่วน DATA SCHEMA ด้านล่าง) — ห้ามถามหาลิงก์ Sheet อีก

ทำงานเป็นขั้นตอนโต้ตอบ และห้ามข้ามขั้นตอน:

[Step 1: วิเคราะห์ข้อมูลและเสนอตัวชี้วัด]
อ่านโครงสร้างคอลัมน์จาก DATA SCHEMA แล้วเสนอ "ตัวชี้วัด (KPI)" และ "กราฟ" ที่เหมาะกับข้อมูลชุดนี้ ให้ฉันเลือกเป็นข้อ A, B, C, D (เลือกได้หลายข้อ หรือพิมพ์เพิ่มเอง) — ในขั้นนี้ห้ามเขียนโค้ด
นำเสนอเป็นรายการ (markdown list) อ่านง่าย — ห้ามใช้แท็ก HTML เช่น <br> และห้ามยัดหลายบรรทัดไว้ในเซลล์ตารางเดียว ถ้าจะแยกบรรทัดให้ใช้ bullet ย่อยแทน

[Step 2: เลือกธีมและเลย์เอาต์]
นำเสนอธีม เช่น Light การ์ดสะอาดตา, Dark สไตล์ Analytics, หรือโทนสีองค์กร ให้ฉันเลือก — ยังไม่ต้องเขียนโค้ด

[Step 3: สร้างแดชบอร์ด]
เมื่อฉันยืนยัน ให้เจนไฟล์ HTML เดียวจบทันที (ห้ามตอบแค่ว่าเดี๋ยวทำ ให้ส่งโค้ดเต็มในข้อความเดียว):
- ส่งโค้ดทั้งหมดในบล็อก \`\`\`html เดียว เริ่มด้วย <!doctype html> — ห้ามใช้ React, JSX, import/export โดยเด็ดขาด (ต้องเป็น HTML+JS ล้วนที่เปิดในเบราว์เซอร์ได้ทันที)
- ใช้ Tailwind CDN + Chart.js (https://cdn.jsdelivr.net/npm/chart.js) + ฟอนต์ Prompt
- ประกาศข้อมูลด้วยบรรทัดนี้เป๊ะๆ ครั้งเดียว: const DATA = __SHEET_DATA__;  // ระบบจะแทนข้อมูลจริงให้เอง ห้ามแก้/ลบ placeholder นี้ และห้ามเขียนข้อมูลตัวอย่างเองหรือใส่ ... (จุดไข่ปลา) ในอาเรย์โดยเด็ดขาด เพราะจะทำให้โค้ดรันไม่ได้
- มี KPI cards ด้านบน, กราฟ 2-4 อัน, และตารางข้อมูลที่ค้นหา/กรองได้
- responsive สวยงาม และคำนวณค่าทุกอย่างจาก DATA จริง ห้าม hardcode ตัวเลข

เริ่มที่ [Step 1] โดยอ้างอิงคอลัมน์จริงจาก DATA SCHEMA ได้เลย`,
  },
];
const CANVAS_REACT_LANGS = new Set(["jsx", "tsx", "javascript", "js", "typescript", "ts"]);
// ─── Canvas Sandbox Architecture ───────────────────────────────────────────
// Standard approach (same as Gemini / Claude Artifacts):
//   Use a Blob URL instead of srcdoc.
//
//   srcdoc  → origin = null (opaque)  → localStorage blocked (SecurityError)
//   blob:   → origin = blob:<parent-origin>/<uuid>  (isolated, unique per render)
//             With allow-same-origin the iframe gets ITS OWN isolated storage,
//             NOT the parent app's localStorage → safe + fully functional.
//
// Sandbox flags:
//   allow-scripts          – run JavaScript (event handlers, animations, etc.)
//   allow-same-origin      – safe with blob URL; gives isolated localStorage
//   allow-forms            – form submit
//   allow-modals           – alert / confirm / prompt
//   allow-popups           – window.open / new tab
//   allow-popups-to-escape-sandbox – popups not sandboxed
//   allow-pointer-lock     – games / interactive demos
//   allow-downloads        – file downloads from the preview
//
//   ✗ allow-top-navigation   – intentionally excluded (cannot navigate parent)
//   ✗ allow-top-navigation-by-user-activation – excluded
// ───────────────────────────────────────────────────────────────────────────
const CANVAS_SANDBOX_POLICY = [
  "allow-scripts",
  "allow-same-origin",      // safe: blob URL has isolated origin ≠ parent origin
  "allow-forms",
  "allow-modals",
  "allow-popups",
  "allow-popups-to-escape-sandbox",
  "allow-pointer-lock",
  "allow-downloads",
].join(" ");

// Fix Tailwind config ordering — AI sometimes puts tailwind.config = {...} before
// the CDN script loads, causing ReferenceError: tailwind is not defined.
// This moves any tailwind.config script to immediately after the CDN script.
const fixTailwindOrder = (html = "") => {
  if (!html || !html.includes("tailwind.config")) return html;
  const cdnRe = /(<script[^>]*cdn\.tailwindcss\.com[^>]*><\/script>)/i;
  if (!cdnRe.test(html)) return html;
  // Extract all inline script blocks that set tailwind.config
  const configRe = /<script(?![^>]*src)[^>]*>[\s\S]*?tailwind\.config[\s\S]*?<\/script>/gi;
  const configBlocks = [];
  const stripped = html.replace(configRe, (m) => { configBlocks.push(m); return ""; });
  if (!configBlocks.length) return html;
  // Re-insert them right after the CDN script tag
  return stripped.replace(cdnRe, (cdn) => cdn + "\n" + configBlocks.join("\n"));
};

// Inject smart link handler into HTML canvas:
//   anchor #id / relative path  -> scroll or navigate inside iframe
//   http:// https://             -> open in new tab safely
const injectLinkHandler = (html = "") => {
  if (!html) return html;
  // Build closing tag split to avoid the parser treating it as a real closing tag
  const closeTag = "</" + "script>";
  const script =
    "<script>(function(){" +
    "document.addEventListener('click',function(e){" +
    "var a=e.target.closest('a');if(!a)return;" +
    "var h=(a.getAttribute('href')||'').trim();" +
    "if(!h||h.charAt(0)==='#'||h.charAt(0)==='/')return;" +
    "try{var u=new URL(h,location.href);" +
    "if(u.protocol==='http:'||u.protocol==='https:'){" +
    "e.preventDefault();window.open(h,'_blank','noopener,noreferrer');}}" +
    "catch(ex){}" +
    "},true);" +
    "}());" +
    closeTag;
  // Insert before the LAST </body> (or </html>). Using lastIndexOf avoids
  // matching a </body> that appears inside page content (e.g. a code sample),
  // which previously caused the script to render as visible text.
  const lower = html.toLowerCase();
  const bodyIdx = lower.lastIndexOf("</body>");
  if (bodyIdx !== -1) {
    return html.slice(0, bodyIdx) + script + "\n" + html.slice(bodyIdx);
  }
  const htmlIdx = lower.lastIndexOf("</html>");
  if (htmlIdx !== -1) {
    return html.slice(0, htmlIdx) + script + "\n" + html.slice(htmlIdx);
  }
  return html + "\n" + script;
};

// Find where a code/HTML artifact begins inside a (possibly partial) stream.
// Returns the earliest index of a fenced block or raw HTML document, or -1.
const findCanvasCodeStart = (s = "") => {
  const lower = String(s || "").toLowerCase();
  const candidates = [
    lower.indexOf("```"),
    lower.indexOf("<!doctype"),
    lower.indexOf("<html"),
  ].filter((i) => i >= 0);
  return candidates.length ? Math.min(...candidates) : -1;
};

const extractCanvasArtifactFromContent = (rawContent = "") => {
  const content = String(rawContent || "").trim();
  if (!content) return null;

  let latestArtifact = null;

  // Split by triple backticks — avoids regex issues on very long AI responses
  const segments = content.split("```");
  // Odd-indexed segments (1, 3, 5, …) are inside code fence pairs
  for (let i = 1; i < segments.length; i += 2) {
    const block = segments[i];
    const nlIdx = block.indexOf("\n");
    if (nlIdx === -1) continue;

    const lang = block.slice(0, nlIdx).trim().toLowerCase();
    const code = block.slice(nlIdx + 1).replace(/\s+$/, ""); // trimEnd
    if (!code) continue;

    if (lang === "html" || lang === "htm") {
      latestArtifact = { kind: "html", language: "html", code };
    } else if (CANVAS_REACT_LANGS.has(lang)) {
      // A full HTML document mislabeled as ```js/```jsx must render as HTML (iframe),
      // not in the React/Sandpack sandbox — otherwise a syntax error shows a crash overlay.
      const codeTrimmed = code.trim();
      if (/^(<!doctype\s+html|<html\b|<head\b|<body\b)/i.test(codeTrimmed)) {
        latestArtifact = { kind: "html", language: "html", code: codeTrimmed };
      } else {
        latestArtifact = { kind: "react", language: lang || "jsx", code };
      }
    } else if (!lang) {
      const codeTrimmed = code.trim();
      // Infer HTML from doc/element patterns
      if (/^(<!doctype\s+html|<html\b|<head\b|<body\b)/i.test(codeTrimmed)) {
        latestArtifact = { kind: "html", language: "html", code: codeTrimmed };
      } else if (
        /(\bfrom\s+["']react["']|\bReactDOM\b|function\s+[A-Z]\w*\s*\(|const\s+[A-Z]\w*\s*=\s*\(|return\s*\(\s*<)/.test(
          codeTrimmed
        )
      ) {
        latestArtifact = { kind: "react", language: "jsx", code: codeTrimmed };
      }
    }
  }

  if (latestArtifact) return latestArtifact;

  // Raw HTML fallback (no fence at all). Match a complete document first; if the
  // response was truncated (no closing tag), grab from the opening tag to the end
  // so incomplete code still goes to the Canvas instead of leaking into the chat.
  const doctypeFull = content.match(/<!doctype\s+html[\s\S]*?<\/html>/i);
  if (doctypeFull?.[0]) {
    return { kind: "html", language: "html", code: doctypeFull[0].trim() };
  }
  const htmlFull = content.match(/<html[\s\S]*?<\/html>/i);
  if (htmlFull?.[0]) {
    return { kind: "html", language: "html", code: htmlFull[0].trim() };
  }
  const doctypeOpen = content.match(/<!doctype\s+html[\s\S]*$/i);
  if (doctypeOpen?.[0]) {
    return { kind: "html", language: "html", code: doctypeOpen[0].trim() };
  }
  const htmlOpen = content.match(/<html[\s\S]*$/i);
  if (htmlOpen?.[0]) {
    return { kind: "html", language: "html", code: htmlOpen[0].trim() };
  }

  return null;
};

// Remove the canvas code from an assistant reply, leaving only the
// explanation/prose. Handles both fenced code blocks and raw HTML documents.
const stripCanvasCodeFromText = (rawContent = "") => {
  let text = String(rawContent || "");
  if (text.includes("```")) {
    const segs = text.split("```");
    // Even-indexed segments are outside code fences (handles unclosed fences too)
    text = segs.filter((_, i) => i % 2 === 0).join("\n").trim();
  }
  // Remove any remaining raw HTML document — complete first, then open-ended
  // (truncated code with no closing tag) so no code leaks into the chat bubble.
  text = text
    .replace(/<!doctype\s+html[\s\S]*?<\/html>/gi, "")
    .replace(/<html[\s\S]*?<\/html>/gi, "")
    .replace(/<!doctype\s+html[\s\S]*$/gi, "")
    .replace(/<html[\s\S]*$/gi, "")
    .trim();
  return text;
};

// Reconstruct canvas fields for assistant messages loaded from the DB.
// The DB only stores raw markdown content (with the ```html block```), so we
// re-derive canvasCode/canvasKind/canvasTitle so the chat shows the artifact
// card (with an "เปิด"/Open button) instead of a raw code block.
const hydrateCanvasMessage = (message = {}) => {
  if (!message || message.role !== "assistant") return message;
  if (message.canvasCode) return message; // already hydrated (fresh stream)
  const artifact = extractCanvasArtifactFromContent(message.content || "");
  if (!artifact?.code) return message;
  const titleMatch = artifact.code.match(/<title[^>]*>([^<]*)<\/title>/i);
  const canvasTitle = titleMatch?.[1]?.trim() || "ตัวอย่าง HTML CODE";
  let canvasTimestamp = null;
  if (message.ts) {
    const t = new Date(message.ts).getTime();
    if (Number.isFinite(t)) canvasTimestamp = t;
  }
  return {
    ...message,
    content: stripCanvasCodeFromText(message.content || ""),
    canvasCode: artifact.code,
    canvasKind: artifact.kind || "html",
    canvasTitle,
    canvasTimestamp,
  };
};

const initModelSettings = (models) => {
  const obj = {};
  models.forEach((m) => (obj[m.name] = { ...defaultSettings }));
  return obj;
};

const OCR_CONTEXT_MARKER = "[Document OCR Context]";
const createInitialOcrProgress = () => ({
  active: false,
  phase: "",
  fileName: "",
  totalFiles: 0,
  doneFiles: 0,
  errorFiles: 0,
  donePages: 0,
  currentPage: 0,
  totalPages: 0,
  tokenPreview: "",
  tokensEstimate: 0,
  percent: 0,
});
const trimAtNaturalBoundary = (text = "", maxLen = 10000) => {
  const raw = String(text || "");
  if (raw.length <= maxLen) return raw;
  const cut = raw.slice(0, Math.max(0, maxLen));
  const lastBreak = Math.max(
    cut.lastIndexOf("\n\n"),
    cut.lastIndexOf("\n"),
    cut.lastIndexOf(" ")
  );
  return (lastBreak > Math.floor(maxLen * 0.6) ? cut.slice(0, lastBreak) : cut).trimEnd();
};
const getUploadErrorMessageForUser = (status, serverMessage = "") => {
  const msg = String(serverMessage || "").trim();
  if (status === 413) {
    return "ไฟล์มีขนาดเกิน 5MB ต่อไฟล์ กรุณาลดขนาดไฟล์แล้วลองใหม่";
  }
  if (status === 401 || status === 403) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่แล้วลองอัปโหลดอีกครั้ง";
  }
  if (status === 502) {
    return "ระบบอ่านเอกสารไม่พร้อมใช้งานชั่วคราว กรุณาลองใหม่อีกครั้งในอีกสักครู่";
  }
  if (status >= 500) {
    return "เซิร์ฟเวอร์มีปัญหาชั่วคราว กรุณาลองใหม่ หากยังไม่สำเร็จให้ติดต่อผู้ดูแลระบบ";
  }
  if (msg) return msg;
  return "อัปโหลดไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
};
const getChatErrorMessageForUser = (rawMessage = "") => {
  const msg = String(rawMessage || "").trim();
  if (!msg) return "ระบบมีปัญหาชั่วคราว กรุณาลองใหม่อีกครั้ง";
  if (/quota exceeded/i.test(msg)) {
    return "โควต้าของโมเดลขั้นสูงหมดแล้วในวันนี้ กรุณาลองใหม่ หรือเปลี่ยนไปใช้โมเดลฟรี";
  }
  if (/prompt is required|message is required/i.test(msg)) {
    return "ระบบวิเคราะห์เอกสารขัดข้องชั่วคราว กรุณาลองส่งใหม่อีกครั้ง";
  }
  if (/openrouter stream aborted|the user aborted a request/i.test(msg)) {
    return "การเชื่อมต่อโมเดลขาดช่วงชั่วคราว ระบบกำลังสลับโมเดลสำรองให้ กรุณาลองส่งใหม่อีกครั้ง";
  }
  if (/doc-processor unavailable|OCR_STREAM_FAILED/i.test(msg)) {
    return "ระบบอ่านเอกสารยังไม่พร้อมใช้งาน กรุณาลองใหม่ในอีกสักครู่";
  }
  if (/timeout|timed out|network/i.test(msg)) {
    return "การเชื่อมต่อช้าเกินกำหนด กรุณาลองใหม่อีกครั้ง";
  }
  return msg;
};

const sanitizeMessageAttachments = (attachments) => {
  if (!Array.isArray(attachments)) return [];
  return attachments
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const name = String(item.name || "").trim();
      if (!name) return null;
      const extGuess = String(item.ext || "").trim().toLowerCase()
        || ((name.split(".").pop() || "").toLowerCase());
      const sizeRaw = Number(item.size || 0);
      return {
        name,
        ext: extGuess || "file",
        size: Number.isFinite(sizeRaw) && sizeRaw > 0 ? Math.round(sizeRaw) : 0,
        fileId: String(item.fileId || item.file_id || "").trim(),
        mimeType: String(item.mimeType || item.mime_type || "").trim(),
      };
    })
    .filter(Boolean)
    .slice(0, 3);
};

const stripOcrContextForDisplay = (message = {}) => {
  if (!message || message.role !== "user" || typeof message.content !== "string") {
    return {
      ...message,
      attachments: sanitizeMessageAttachments(message?.attachments),
    };
  }
  const raw = message.content;
  const markerIndex = raw.indexOf(OCR_CONTEXT_MARKER);
  if (markerIndex < 0) {
    return {
      ...message,
      attachments: sanitizeMessageAttachments(message.attachments),
    };
  }
  const visible = raw.slice(0, markerIndex).trim();
  return {
    ...message,
    content: visible || "โปรดวิเคราะห์เอกสารที่แนบมา",
    attachments: sanitizeMessageAttachments(message.attachments),
  };
};

/* =========================================================
   MAIN COMPONENT
========================================================= */
export default function AppChat() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cid } = useParams();
  const { toast } = useToast();

  /* ---------------- MODELS ---------------- */
  const P = process.env.PUBLIC_URL || "";
  const parseEnvModelList = (raw, fallback = []) => {
    const source = String(raw || "").trim();
    if (!source) return fallback;
    const parsed = source
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    return parsed.length ? parsed : fallback;
  };
  const formatModelTitle = (modelId = "") => {
    const raw = String(modelId || "");
    const tail = raw.includes("/") ? raw.split("/").pop() : raw;
    if (!tail) return raw;
    return tail.charAt(0).toUpperCase() + tail.slice(1);
  };
  const freeOssModelVersions = parseEnvModelList(
    process.env.REACT_APP_FREE_OSS_MODELS,
    ["oss-120b"]
  );
  const toFreeOssDisplayVersion = (version = "") => {
    const v = String(version || "").trim().toLowerCase();
    // Keep backend routing on oss-120b, but show a friendlier marketing label in UI.
    if (v === "oss-120b") return "oss-20b";
    return version;
  };
  const freeOssModels = freeOssModelVersions.map((version) => ({
    name: `OSS: ${formatModelTitle(toFreeOssDisplayVersion(version))}`,
    version,
    provider: "vllm",
    icon: `${P}/chatgpt-icon.png`,
    description: "สำหรับงานทั่วไป",
  }));
  const models = [
    ...freeOssModels,
    {
      name: "OpenAI",
      version: "openai/gpt-5.5-pro",
      provider: "openrouter",
      icon: `${P}/chatgpt-icon.png`,
      description: "เด่นด้านตรรกะและงานโค้ด",
    },
    {
      name: "Claude",
      version: "anthropic/claude-sonnet-4.5",
      provider: "openrouter",
      icon: `${P}/claude-ai-icon.png`,
      description: "เด่นด้านเขียน/สรุปเอกสาร",
    },
    {
      name: "Perplexity",
      version: "perplexity/sonar",
      provider: "openrouter",
      icon: `${P}/perplexity-ai-icon.png`,
      description: "เด่นด้านค้นหาและอ้างอิงข้อมูล",
    },
    {
      name: "Gemini",
      version: "google/gemini-2.5-pro",
      provider: "openrouter",
      icon: `${P}/gemini-icon.png`,
      description: "เด่นด้าน reasoning และงานซับซ้อน",
    },
  ];
  const paidModelSuggestions = models.filter((m) => m.provider === "openrouter").slice(0, 3);
  const defaultModel = models.find((m) => m.provider === "vllm") || paidModelSuggestions[0] || models[0];
  const findModelByRoute = (provider, model) =>
    models.find((m) => m.provider === provider && m.version === model) || defaultModel;

  const [selectedModel, setSelectedModel] = useState(defaultModel);
  const [isModelConfirmed, setIsModelConfirmed] = useState(false);
  const [modelSettings, setModelSettings] = useState(initModelSettings(models));
  const [usage, setUsage] = useState({});
  const [quotaNoticeModal, setQuotaNoticeModal] = useState(null);
  const [quotaNoticeSeenDate, setQuotaNoticeSeenDate] = useState(null);
  const [conversationLocks, setConversationLocks] = useState({});

  /* ---------------- UI STATE ---------------- */
  const [activeMenu, setActiveMenu] = useState("chat");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(DESKTOP_SIDEBAR_COLLAPSE_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [isAdmin, setIsAdmin] = useState(false);

  /* ---------------- CHAT ---------------- */
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState([]);
  // Google Sheet → dashboard (Canvas inspiration)
  const [sheetModalOpen, setSheetModalOpen] = useState(false);
  const [sheetUrl, setSheetUrl] = useState("");
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetError, setSheetError] = useState("");
  const sheetDataRef = useRef(null); // full rows pending __SHEET_DATA__ injection
  const sheetPromptRef = useRef(""); // mega prompt to send after fetch
  const [ocrStatusText, setOcrStatusText] = useState("");
  const [ocrPageStateByFile, setOcrPageStateByFile] = useState({});
  const [isOcrDetailsExpanded, setIsOcrDetailsExpanded] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(createInitialOcrProgress);
  const [thinking, setThinking] = useState(false);
  const [awaitingFirstToken, setAwaitingFirstToken] = useState(false);
  const [llmStatusText, setLlmStatusText] = useState("");
  const [llmWaitingSeconds, setLlmWaitingSeconds] = useState(0);
  const [liveSourceCount, setLiveSourceCount] = useState(0); // reference sources found so far (live)
  const [activeChat, setActiveChat] = useState(null);
  const chatEndRef = useRef(null);
  const chatScrollRef = useRef(null);
  const shouldStickToBottomRef = useRef(true);
  const isSendingRef = useRef(false);
  const abortControllerRef = useRef(null);
  const uploadControllersRef = useRef(new Map());

  /* ---------------- DATA LISTS ---------------- */
  const [chatList, setChatList] = useState([]);
  const [search, setSearch] = useState("");
  const [menuOpen, setMenuOpen] = useState(null);

  /* ---------------- RESPONSE MODE ---------------- */
  const [responseMode, setResponseMode] = useState("normal");
  const [composerTool, setComposerTool] = useState("");

  const scrollToBottom = (behavior = "auto") => {
    const el = chatScrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  };

  const handleChatScroll = () => {
    const el = chatScrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldStickToBottomRef.current = distanceFromBottom < 80;
  };

  useEffect(() => {
    const waiting = thinking && awaitingFirstToken;
    if (!waiting) {
      setLlmWaitingSeconds(0);
      return;
    }
    const timer = setInterval(() => {
      setLlmWaitingSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [thinking, awaitingFirstToken]);

  /* ---------------- Notifications ---------------- */
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      title: "ระบบอัปเดตสำเร็จ",
      message: "PSRU AI Hub อัปเดตเวอร์ชันใหม่แล้ว!",
      time: "เมื่อ 5 นาทีที่แล้ว",
      read: false,
    },
    {
      id: 2,
      title: "แจ้งเตือนการใช้งาน",
      message: "คุณใช้โควต้าประจำวันไปแล้ว 80%",
      time: "เมื่อ 30 นาทีที่แล้ว",
      read: false,
    },
  ]);

  /* ---------------- Settings Panel ---------------- */
  const activeSettings = modelSettings[selectedModel.name];
  const activeModelLabel = selectedModel?.name || "Model";

  const updateSetting = (modelName, key, value) => {
    setModelSettings((prev) => ({
      ...prev,
      [modelName]: { ...prev[modelName], [key]: value },
    }));
  };

  const updateReasoning = (modelName, key, value) => {
    setModelSettings((prev) => ({
      ...prev,
      [modelName]: {
        ...prev[modelName],
        reasoning: { ...prev[modelName].reasoning, [key]: value },
      },
    }));
  };

  const resetSettings = (modelName) => {
    setModelSettings((prev) => ({
      ...prev,
      [modelName]: { ...defaultSettings },
    }));
  };

  /* =========================================================
       API: LOAD CHAT LIST & USAGE
    ========================================================== */
  const loadConversations = async () => {
    try {
      const res = await fetch(`${API_BASE}/conversation`, {
        headers: { Authorization: `Bearer ${lsGet("token")}` },
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setChatList(data);
        const nextLocks = {};
        for (const row of data) {
          if (row?.locked_provider && row?.locked_model) {
            nextLocks[row.cid] = {
              provider: row.locked_provider,
              model: row.locked_model,
              lockedAt: row.locked_at || null,
            };
          }
        }
        setConversationLocks(nextLocks);
      }
    } catch (e) {
      console.error("loadConversations error:", e);
    }
  };

  const loadUsage = async () => {
    try {
      const res = await fetch(`${API_BASE}/token/usage`, {
        headers: { Authorization: `Bearer ${lsGet("token")}` },
      });
      const data = await res.json();
      setUsage(data);
    } catch (e) {
      console.error("loadUsage error:", e);
    }
  };

  const loadAdminAccess = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/access`, {
        headers: { Authorization: `Bearer ${lsGet("token")}` },
      });
      if (!res.ok) {
        setIsAdmin(false);
        return;
      }
      const data = await res.json();
      setIsAdmin(Boolean(data?.is_admin));
    } catch (e) {
      console.error("loadAdminAccess error:", e);
      setIsAdmin(false);
    }
  };

  /* ---------------- API: LOAD CHAT MESSAGES ---------------- */
  const loadChatMessages = async (cid) => {
    if (loadChatMessages.loading) return;
    loadChatMessages.loading = true;

    try {
      const res = await fetch(`${API_BASE}/conversation/${cid}`, {
        headers: { Authorization: `Bearer ${lsGet("token")}` },
      });

      if (res.status === 404) {
        toast({
          variant: "error",
          title: "ไม่พบแชทนี้",
          message: "แชทที่คุณเปิดอาจถูกลบ หรือไม่มีอยู่ในระบบ",
          duration: 4000,
        });
        loadChatMessages.loading = false;
        setTimeout(() => navigate("/"), 500);
        return;
      }

      if (!res.ok) throw new Error(`Failed to load: ${res.status}`);

      const data = await res.json();
      const lock = data?.meta?.lock;
      if (lock?.provider && lock?.model) {
        setConversationLocks((prev) => ({
          ...prev,
          [cid]: {
            provider: lock.provider,
            model: lock.model,
            lockedAt: lock.lockedAt || null,
          },
        }));
        const lockedModel = findModelByRoute(lock.provider, lock.model);
        setSelectedModel(lockedModel);
        setIsModelConfirmed(true);
      }
      const nextMessages = Array.isArray(data.messages)
        ? data.messages.map(stripOcrContextForDisplay).map(hydrateCanvasMessage)
        : [];
      if (!lock?.provider && !lock?.model) {
        // Legacy conversations without lock should remain usable when they already have messages.
        setIsModelConfirmed(nextMessages.length > 0);
      }
      setMessages((prev) => {
        // Prevent route-load race from wiping optimistic user message
        // while a fresh request is being sent on a newly created conversation.
        if (isSendingRef.current && prev.length > 0 && nextMessages.length === 0) {
          return prev;
        }
        return nextMessages;
      });
    } catch (e) {
      console.error("loadChatMessages error:", e);
    } finally {
      loadChatMessages.loading = false;
    }
  };

  /* =========================================================
       API: CREATE NEW CHAT
    ========================================================== */
  const handleCreateNewChat = async (
    preferredModel = selectedModel,
    { confirmModel = false, resetComposerOnCreate = true } = {}
  ) => {
    try {
      if (resetComposerOnCreate) {
        for (const [, uploadCtrl] of uploadControllersRef.current.entries()) {
          try {
            uploadCtrl.abort();
          } catch {
            // ignore per-upload abort errors
          }
        }
        uploadControllersRef.current.clear();
        setInput("");
        setAttachments([]);
        sheetDataRef.current = null; // drop any pending Google Sheet data
        setComposerTool(""); // explicit new chat exits Canvas mode
        setOcrStatusText("");
        setOcrPageStateByFile({});
        setIsOcrDetailsExpanded(false);
        setOcrProgress(createInitialOcrProgress());
      }
      if (preferredModel) setSelectedModel(preferredModel);
      setIsModelConfirmed(Boolean(confirmModel));
      const res = await fetch(`${API_BASE}/conversation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${lsGet("token")}`,
        },
      });
      const data = await res.json();

      if (data.cid) {
        const newChat = {
          id: data.cid,
          cid: data.cid,
          title: "แชทใหม่",
          model: null,
          messages: [],
        };
        setChatList((prev) => [newChat, ...prev]);
        setActiveChat(newChat);
        setMessages([]);
        navigate(`/c/${data.cid}`);
        return data.cid;
      }
    } catch (e) {
      console.error("create chat error:", e);
    }
  };

  /* =========================================================
       API: SEND MESSAGE
    ========================================================== */
  const sendMessage = async (overrideText) => {
    // overrideText may be a string (programmatic send, e.g. inspiration chip).
    // Guard against the click/keyboard event that onClick={sendMessage} passes.
    const override = typeof overrideText === "string" ? overrideText : null;
    const effectiveInput = override != null ? override : input;
    if (!effectiveInput.trim() && attachments.length === 0) return;
    const hasPendingUpload = attachments.some(
      (a) => a.state === "selected" || a.state === "uploading"
    );
    if (hasPendingUpload) {
      toast({
        variant: "error",
        title: "ไฟล์กำลังอัปโหลด",
        message: "กรุณารอให้อัปโหลดไฟล์เสร็จก่อนกดส่ง",
        duration: 2200,
      });
      return;
    }
    if (thinking || isSendingRef.current) return;
    const currentCid = cid || activeChat?.id;
    const currentLock = currentCid ? conversationLocks[currentCid] : null;
    const mustConfirmModelFirst =
      !currentLock && messages.length === 0 && !isModelConfirmed;
    if (mustConfirmModelFirst) {
      toast({
        variant: "error",
        title: "กรุณาเลือกโมเดลก่อน",
        message: "ต้องเลือกโมเดลก่อนเริ่มพิมพ์และส่งข้อความแรก",
        duration: 2500,
      });
      return;
    }
    isSendingRef.current = true;
    try {
      let currentCid = cid || activeChat?.id;

      if (!currentCid && location.pathname === "/") {
        const newCid = await handleCreateNewChat(selectedModel, {
          confirmModel: isModelConfirmed,
          resetComposerOnCreate: false,
        });
        if (!newCid) return;
        currentCid = newCid;
      }

      if (!currentCid) return;
      await actuallySendMessage(currentCid, override);
    } finally {
      isSendingRef.current = false;
    }
  };

  const stopMessage = () => {
    const controller = abortControllerRef.current;
    try {
      if (controller) {
        controller.abort();
      }
      for (const [, uploadCtrl] of uploadControllersRef.current.entries()) {
        try {
          uploadCtrl.abort();
        } catch {
          // ignore per-upload abort errors
        }
      }
      uploadControllersRef.current.clear();
      setOcrStatusText("ยกเลิกการประมวลผลแล้ว");
      setLlmStatusText("");
      setComposerTool("");
      setOcrPageStateByFile({});
      setIsOcrDetailsExpanded(false);
      setAttachments([]);
      setOcrProgress((prev) => ({
        ...prev,
        active: false,
        phase: "cancelled",
      }));
    } catch {
      // ignore abort errors
    }
  };

  const actuallySendMessage = async (chatId, overrideText = null) => {
    const rawInput = typeof overrideText === "string" ? overrideText : input;
    const userText = rawInput.trim() ? rawInput : "โปรดวิเคราะห์เอกสารที่แนบมา";
    const isCanvasMode = composerTool === COMPOSER_TOOL_CANVAS;
    const useStreaming = Boolean(activeSettings?.streaming);
    const lock = conversationLocks[chatId];
    const effectiveProvider = lock?.provider || selectedModel.provider;
    const effectiveModel = lock?.model || selectedModel.version;
    const requestedProvider = String(effectiveProvider || "").toLowerCase();
    const paidUsedPercent = Number(
      usage?.paid_used_percent ?? usage?.used_percent ?? 0
    );
    const usageDateKey =
      usage?.date || usage?.last_reset_date || "session";
    const newUserMsg = { role: "user", content: userText };
    shouldStickToBottomRef.current = true;
    setMessages((prev) => [...prev, newUserMsg]);
    setInput("");
    setThinking(true);
    setAwaitingFirstToken(true);
    setLiveSourceCount(0);
    setLlmStatusText(isCanvasMode ? "กำลังเขียนโค้ด..." : "กำลังประมวลผลคำขอของคุณ...");
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const runOcrForAttachment = async (att) => {
      if (!att.fileId) {
        throw new Error(`ไฟล์ ${att.name} ยังไม่พร้อมใช้งาน (ไม่มี file_id)`);
      }
      setAttachments((prev) =>
        prev.map((x) =>
          x.id === att.id
            ? {
                ...x,
                state: "processing",
                progress: { totalPages: 0, donePages: 0, tokensEstimate: 0 },
              }
            : x
        )
      );
      setOcrStatusText(`กำลังอ่านข้อมูลของ ${att.name}`);
      setOcrPageStateByFile((prev) => ({ ...prev, [att.id]: { totalPages: 0, states: [] } }));
      setOcrProgress((prev) => ({
        ...prev,
        active: true,
        phase: "processing",
        fileName: att.name,
        donePages: 0,
        currentPage: 0,
        totalPages: 0,
        tokenPreview: "",
        tokensEstimate: 0,
      }));

      const streamRes = await fetch(`${API_BASE}/ocr/process/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${lsGet("token")}`,
        },
        body: JSON.stringify({ file_id: att.fileId, file_name: att.name }),
        signal: abortController.signal,
      });
      if (!streamRes.ok || !streamRes.body) {
        let errText = `HTTP ${streamRes.status}`;
        try {
          errText = await streamRes.text();
        } catch {
          // ignore
        }
        throw new Error(`OCR stream failed: ${errText}`);
      }

      const reader = streamRes.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let extractedMarkdown = "";
      let donePages = 0;
      let totalPages = 0;
      let docTotalPages = 0;
      let lastTokensEstimate = 0;

      const handleEvent = (name, payload) => {
        if (name === "request_start") {
          setOcrStatusText(`กำลังอ่านข้อมูลของ ${att.name}`);
          setOcrProgress((prev) => ({
            ...prev,
            active: true,
            phase: "request_start",
            fileName: att.name,
            tokenPreview: "",
            tokensEstimate: 0,
          }));
        } else if (name === "upload_done") {
          setOcrStatusText(`กำลังอ่านข้อมูลของ ${att.name}`);
          setOcrProgress((prev) => ({
            ...prev,
            active: true,
            phase: "upload_done",
            fileName: att.name,
            tokenPreview: "",
            tokensEstimate: 0,
          }));
        } else if (name === "classify_done") {
          totalPages = Number(payload?.total_pages || 0);
          if (totalPages > 0) {
            setOcrPageStateByFile((prev) => ({
              ...prev,
              [att.id]: {
                totalPages,
                states: Array.from({ length: totalPages }, () => "pending"),
                pageTokens: Array.from({ length: totalPages }, () => 0),
              },
            }));
          }
          setAttachments((prev) =>
            prev.map((x) =>
              x.id === att.id
                ? {
                    ...x,
                    progress: {
                      ...(x.progress || {}),
                      totalPages,
                      donePages,
                      tokensEstimate: lastTokensEstimate,
                    },
                  }
                : x
            )
          );
          if (totalPages > 0) {
            setOcrStatusText(`กำลังอ่านข้อมูลของ ${att.name}`);
            setOcrProgress((prev) => ({
              ...prev,
              active: true,
              phase: "classify_done",
              fileName: att.name,
              totalPages,
              currentPage: donePages,
              donePages,
              tokenPreview: "",
              tokensEstimate: lastTokensEstimate,
            }));
          }
        } else if (name === "page_start") {
          const pageIndex = Number(payload?.page_index ?? -1);
          setOcrStatusText(`กำลังอ่านข้อมูลของ ${att.name}`);
          if (Number.isFinite(pageIndex) && pageIndex >= 0) {
            setOcrPageStateByFile((prev) => {
              const current = prev[att.id] || { totalPages, states: [], pageTokens: [] };
              const states = Array.isArray(current.states) ? [...current.states] : [];
              if (pageIndex < states.length && states[pageIndex] !== "done") {
                states[pageIndex] = "processing";
              }
              return {
                ...prev,
                [att.id]: {
                  totalPages: current.totalPages || totalPages,
                  states,
                  pageTokens: Array.isArray(current.pageTokens) ? [...current.pageTokens] : [],
                },
              };
            });
          }
          const pageValue = Number.isFinite(pageIndex) && pageIndex >= 0 ? pageIndex + 1 : 0;
          setOcrProgress((prev) => ({
            ...prev,
            active: true,
            phase: "page_start",
            fileName: att.name,
            totalPages,
            currentPage: pageValue,
            donePages,
            tokenPreview: "",
            tokensEstimate: lastTokensEstimate,
          }));
        } else if (name === "token") {
          lastTokensEstimate = Number(payload?.tokens_estimate || lastTokensEstimate || 0);
          const pageIndex = Number(payload?.page_index ?? -1);
          setOcrStatusText(`กำลังอ่านข้อมูลของ ${att.name}`);
          if (Number.isFinite(pageIndex) && pageIndex >= 0) {
            setOcrPageStateByFile((prev) => {
              const current = prev[att.id] || { totalPages, states: [], pageTokens: [] };
              const states = Array.isArray(current.states) ? [...current.states] : [];
              const pageTokens = Array.isArray(current.pageTokens) ? [...current.pageTokens] : [];
              if (pageIndex < states.length && states[pageIndex] !== "done") {
                states[pageIndex] = "processing";
              }
              if (pageIndex < Math.max(pageTokens.length, states.length)) {
                pageTokens[pageIndex] = lastTokensEstimate;
              }
              return {
                ...prev,
                [att.id]: {
                  totalPages: current.totalPages || totalPages,
                  states,
                  pageTokens,
                },
              };
            });
          }
          setAttachments((prev) =>
            prev.map((x) =>
              x.id === att.id
                ? {
                    ...x,
                    progress: {
                      ...(x.progress || {}),
                      totalPages,
                      donePages,
                      tokensEstimate: lastTokensEstimate,
                    },
                  }
                : x
            )
          );
          setOcrProgress((prev) => ({
            ...prev,
            active: true,
            phase: "token",
            fileName: att.name,
            totalPages,
            currentPage: Math.max(prev.currentPage || 0, donePages + 1),
            donePages,
            tokenPreview: "",
            tokensEstimate: lastTokensEstimate,
          }));
        } else if (name === "page_done") {
          donePages += 1;
          const pageIndex = Number(payload?.page_index ?? -1);
          if (Number.isFinite(pageIndex) && pageIndex >= 0) {
            setOcrPageStateByFile((prev) => {
              const current = prev[att.id] || { totalPages, states: [], pageTokens: [] };
              const states = Array.isArray(current.states) ? [...current.states] : [];
              const pageTokens = Array.isArray(current.pageTokens) ? [...current.pageTokens] : [];
              if (pageIndex < states.length) {
                states[pageIndex] = "done";
              }
              if (pageIndex < Math.max(pageTokens.length, states.length)) {
                pageTokens[pageIndex] = Number(payload?.tokens_estimate || lastTokensEstimate || pageTokens[pageIndex] || 0);
              }
              return {
                ...prev,
                [att.id]: {
                  totalPages: current.totalPages || totalPages,
                  states,
                  pageTokens,
                },
              };
            });
          }
          setAttachments((prev) =>
            prev.map((x) =>
              x.id === att.id
                ? {
                    ...x,
                    progress: {
                      ...(x.progress || {}),
                      totalPages,
                      donePages,
                      tokensEstimate: lastTokensEstimate,
                    },
                  }
                : x
            )
          );
          setOcrStatusText(`กำลังอ่านข้อมูลของ ${att.name}`);
          setOcrProgress((prev) => ({
            ...prev,
            active: true,
            phase: "page_done",
            fileName: att.name,
            totalPages: Math.max(prev.totalPages || 0, totalPages),
            currentPage: donePages,
            donePages: Math.max(prev.donePages || 0, donePages),
            tokenPreview: "",
            tokensEstimate: lastTokensEstimate,
          }));
        } else if (name === "page_error") {
          const pageIndex = Number(payload?.page_index ?? -1);
          if (Number.isFinite(pageIndex) && pageIndex >= 0) {
            setOcrPageStateByFile((prev) => {
              const current = prev[att.id] || { totalPages, states: [], pageTokens: [] };
              const states = Array.isArray(current.states) ? [...current.states] : [];
              const pageTokens = Array.isArray(current.pageTokens) ? [...current.pageTokens] : [];
              if (pageIndex < states.length) {
                states[pageIndex] = "error";
              }
              return {
                ...prev,
                [att.id]: {
                  totalPages: current.totalPages || totalPages,
                  states,
                  pageTokens,
                },
              };
            });
          }
          const pageLabel = Number.isFinite(pageIndex) && pageIndex >= 0 ? pageIndex + 1 : "?";
          const detail = payload?.detail || "unknown page error";
          throw new Error(`OCR page ${pageLabel} failed: ${detail}`);
        } else if (name === "document_done") {
          extractedMarkdown = payload?.extracted_markdown || "";
          docTotalPages = Number(payload?.total_pages || 0);
          setOcrStatusText(`OCR เสร็จ: ${att.name}`);
          setOcrPageStateByFile((prev) => {
            const current = prev[att.id] || { totalPages, states: [], pageTokens: [] };
            const states = Array.isArray(current.states) ? [...current.states] : [];
            const pageTokens = Array.isArray(current.pageTokens) ? [...current.pageTokens] : [];
            for (let i = 0; i < states.length; i += 1) {
              if (states[i] === "pending" || states[i] === "processing") {
                states[i] = "done";
              }
            }
            return {
              ...prev,
              [att.id]: {
                totalPages: current.totalPages || totalPages,
                states,
                pageTokens,
              },
            };
          });
          setOcrProgress((prev) => ({
            ...prev,
            active: true,
            phase: "document_done",
            fileName: att.name,
            totalPages: totalPages || prev.totalPages,
            currentPage: totalPages || prev.totalPages,
            donePages: totalPages || prev.totalPages || 0,
            tokenPreview: "",
            tokensEstimate: lastTokensEstimate,
          }));
        } else if (name === "error") {
          const detail = payload?.detail || payload?.message || "OCR failed";
          throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split("\n\n");
        buffer = blocks.pop() || "";
        for (const block of blocks) {
          const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
          if (!lines.length) continue;
          let eventName = "message";
          const dataLines = [];
          for (const line of lines) {
            if (line.startsWith("event:")) eventName = line.slice(6).trim();
            else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
          }
          if (!dataLines.length) continue;
          let payload = {};
          try {
            payload = JSON.parse(dataLines.join("\n"));
          } catch {
            payload = {};
          }
          handleEvent(eventName, payload);
        }
      }

      if (!extractedMarkdown.trim()) {
        if (!docTotalPages) {
          throw new Error(
            `เปิดไฟล์ ${att.name} ไม่ได้ (อ่านจำนวนหน้าไม่ได้) — ไฟล์อาจเสียหาย, มีรหัสผ่าน, หรือไม่ใช่ไฟล์ PDF/DOCX ที่ถูกต้อง`
          );
        }
        throw new Error(
          `ไม่พบข้อความในไฟล์ ${att.name} (${docTotalPages} หน้า) — น่าจะเป็นไฟล์สแกน/รูปภาพที่อ่านตัวอักษรไม่ออก ลองใช้ไฟล์ที่เลือก/คัดลอกข้อความได้`
        );
      }

      setAttachments((prev) =>
        prev.map((x) =>
          x.id === att.id
            ? {
                ...x,
                state: "done",
                ocrText: extractedMarkdown,
                progress: {
                  ...(x.progress || {}),
                  totalPages,
                  donePages: totalPages || donePages,
                  tokensEstimate: lastTokensEstimate,
                },
              }
            : x
        )
      );
      return extractedMarkdown;
    };
    const maybeShowPreSwitchWarning = () => {
      const shouldWarn =
        requestedProvider !== "vllm" &&
        paidUsedPercent >= 85 &&
        paidUsedPercent < 100 &&
        quotaNoticeSeenDate !== usageDateKey;
      if (!shouldWarn) return;
      setQuotaNoticeModal({
        type: "warning",
        percent: paidUsedPercent,
      });
      setQuotaNoticeSeenDate(usageDateKey);
    };
    const maybeShowSwitchedNotice = (route = {}) => {
      const actualProvider = String(route?.provider || "").toLowerCase();
      const shouldShow =
        requestedProvider !== "vllm" &&
        actualProvider === "vllm" &&
        quotaNoticeSeenDate !== usageDateKey;
      if (!shouldShow) return;
      const fromModel = findModelByRoute(effectiveProvider, effectiveModel);
      const toModel = findModelByRoute(route.provider, route.model);
      setQuotaNoticeModal({
        type: "switched",
        fromModelName: fromModel?.name || "โมเดลขั้นสูง",
        toModelName: toModel?.name || "โมเดลฟรี",
      });
      setQuotaNoticeSeenDate(usageDateKey);
    };

    try {
      maybeShowPreSwitchWarning();
      let augmentedMessage = userText;
      const activeAttachments = attachments.filter(
        (a) => a.state !== "error" && a.state !== "cancelled"
      );
      const hasReadyAttachment = activeAttachments.some((a) => a.state === "uploaded" || a.state === "done");
      if (!rawInput.trim() && !hasReadyAttachment) {
        throw new Error("ยังไม่มีไฟล์ที่พร้อมประมวลผล กรุณารอให้อัปโหลดเสร็จหรือพิมพ์ข้อความ");
      }
      const payloadAttachments = activeAttachments.map((att) => ({
        name: att.name,
        size: Number(att.size || 0),
        ext: String((att.name || "").split(".").pop() || "").toLowerCase(),
        fileId: att.fileId || "",
        mimeType: att.file?.type || guessAttachmentMime(att.name),
      }));
      if (activeAttachments.length > 0) {
        setOcrProgress((prev) => ({
          ...prev,
          active: true,
          phase: "batch_processing",
          totalFiles: activeAttachments.length,
          doneFiles: 0,
          errorFiles: 0,
          donePages: 0,
          currentPage: 0,
          totalPages: 0,
          tokenPreview: "",
          tokensEstimate: 0,
          percent: 0,
        }));
        const docs = [];
        const stats = { completed: 0, failed: 0 };
        const ocrJobs = activeAttachments.map(async (att) => {
          if (att.state === "uploading" || att.state === "selected") {
            throw new Error(`ไฟล์ ${att.name} ยังอัปโหลดไม่เสร็จ`);
          }
          const text = att.ocrText?.trim() ? att.ocrText : await runOcrForAttachment(att);
          stats.completed += 1;
          setOcrProgress((prev) => {
            const fileRatio = activeAttachments.length > 0 ? stats.completed / activeAttachments.length : 1;
            return {
              ...prev,
              active: true,
              phase: "file_done",
              fileName: att.name,
              totalFiles: activeAttachments.length,
              doneFiles: stats.completed,
              errorFiles: stats.failed,
              percent: Math.max(prev.percent || 0, Math.round(fileRatio * 100)),
            };
          });
          return `### เอกสาร: ${att.name}\n${text}`;
        });

        const failReasons = [];
        const settled = await Promise.allSettled(ocrJobs);
        for (const result of settled) {
          if (result.status === "fulfilled") {
            docs.push(result.value);
          } else {
            stats.failed += 1;
            const reason =
              result.reason?.message ||
              (typeof result.reason === "string" ? result.reason : "") ||
              "ไม่ทราบสาเหตุ";
            failReasons.push(String(reason));
            console.error("OCR job failed:", result.reason);
          }
        }
        if (stats.failed > 0) {
          setOcrProgress((prev) => ({
            ...prev,
            active: true,
            phase: "partial_error",
            errorFiles: stats.failed,
            doneFiles: stats.completed,
          }));
        }
        if (docs.length === 0) {
          // Surface the real upstream reason so the user isn't stuck on a generic message.
          const detail = failReasons[0] ? `: ${failReasons[0]}` : "";
          throw new Error(`อ่านไฟล์ไม่สำเร็จ${detail}`);
        }
        setOcrStatusText("กำลังส่งคำขอไปยัง AI LLM...");
        setOcrProgress((prev) => ({
          ...prev,
          active: true,
          phase: "analyzing",
        }));
        const mergedDocs = docs.join("\n\n");
        const compactDocs = trimAtNaturalBoundary(
          mergedDocs,
          MAX_CLIENT_OCR_CONTEXT_CHARS
        );
        augmentedMessage = `${userText}\n\n[Document OCR Context]\n${compactDocs}`;
      }

      const res = await fetch(`${API_BASE}/chat/${chatId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${lsGet("token")}`,
        },
        signal: abortController.signal,
        body: JSON.stringify({
          provider: effectiveProvider,
          model: effectiveModel,
          mode: "full",
          taskType: isCanvasMode ? "canvas" : "default",
          message: augmentedMessage,
          attachments: payloadAttachments,
          stream: isCanvasMode ? true : useStreaming,
        }),
      });

      if (!res.ok) {
        let errText = `HTTP ${res.status}`;
        try {
          const errData = await res.json();
          errText = errData?.message || errData?.error || errText;
        } catch {
          // keep default error text
        }
        throw new Error(errText);
      }

      const contentType = (res.headers.get("content-type") || "").toLowerCase();
      const isSse = contentType.includes("text/event-stream");

      if (useStreaming && isSse) {
        const streamId = `stream-${Date.now()}`;
        // Reset live-canvas streaming buffers for this turn
        liveCanvasStartedRef.current = false;
        liveCanvasBufRef.current = "";
        lastLivePaintRef.current = 0;
        canvasCodeStartRef.current = -1;
        setLiveCanvasCode("");
        setIsStreamingCanvas(false);
        setMessages((prev) => [
          ...prev,
          {
            id: streamId,
            role: "assistant",
            content: "",
            citations: [],
            tokens: {},
            memory: {},
          },
        ]);

        const reader = res.body?.getReader();
        if (!reader) throw new Error("stream body not readable");

        const decoder = new TextDecoder();
        let buffer = "";
        let finalPayload = null;
        let pendingAssistantText = "";
        let streamPaintTimer = null;
        const applyAssistantChunk = (chunk) => {
          if (!chunk) return;
          setAwaitingFirstToken(false);
          if (isCanvasMode) return;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === streamId ? { ...m, content: `${m.content || ""}${chunk}` } : m
            )
          );
        };

        const paintAssistantText = () => {
          if (!pendingAssistantText) {
            if (streamPaintTimer) {
              clearInterval(streamPaintTimer);
              streamPaintTimer = null;
            }
            return;
          }
          const chunk = pendingAssistantText.slice(0, STREAM_CHARS_PER_TICK);
          pendingAssistantText = pendingAssistantText.slice(chunk.length);
          applyAssistantChunk(chunk);
        };

        const ensurePaintLoop = () => {
          if (streamPaintTimer) return;
          streamPaintTimer = setInterval(paintAssistantText, STREAM_PAINT_INTERVAL_MS);
        };

        // Canvas streaming: prose (e.g. step-by-step questions) streams into the
        // chat bubble; once a code/HTML block begins, the code streams live into
        // the Code tab on the right pane (and the prose-so-far stays in chat).
        const paintCanvasStream = () => {
          const buf = liveCanvasBufRef.current;
          if (canvasCodeStartRef.current < 0) {
            const idx = findCanvasCodeStart(buf);
            if (idx < 0) {
              // Still pure prose — show it in the chat bubble
              setMessages((prev) =>
                prev.map((m) => (m.id === streamId ? { ...m, content: buf } : m))
              );
              return;
            }
            canvasCodeStartRef.current = idx;
          }
          const start = canvasCodeStartRef.current;
          const prose = buf.slice(0, start).trim();
          const code = buf.slice(start);
          setMessages((prev) =>
            prev.map((m) => (m.id === streamId ? { ...m, content: prose } : m))
          );
          if (!liveCanvasStartedRef.current) {
            liveCanvasStartedRef.current = true;
            setIsStreamingCanvas(true);
            setIsCanvasPaneOpen(true);
            setCanvasViewMode("code");
          }
          setLiveCanvasCode(code);
        };

        const appendAssistantText = (textChunk) => {
          if (!textChunk) return;
          if (isCanvasMode) {
            setAwaitingFirstToken(false);
            liveCanvasBufRef.current += textChunk;
            const now = Date.now();
            if (now - lastLivePaintRef.current > 160) {
              lastLivePaintRef.current = now;
              paintCanvasStream();
            }
            return;
          }
          pendingAssistantText += textChunk;
          ensurePaintLoop();
        };

        const flushAssistantText = () => {
          if (!pendingAssistantText) return;
          const chunk = pendingAssistantText;
          pendingAssistantText = "";
          applyAssistantChunk(chunk);
        };

        const applyFinal = (payload = {}) => {
          if (streamPaintTimer) {
            clearInterval(streamPaintTimer);
            streamPaintTimer = null;
          }
          flushAssistantText();
          finalPayload = payload;
          if (payload?.route?.provider && payload?.route?.model) {
            maybeShowSwitchedNotice(payload.route);
            setConversationLocks((prev) => ({
              ...prev,
              [chatId]: {
                provider: payload.route.provider,
                model: payload.route.model,
                lockedAt: prev?.[chatId]?.lockedAt || Date.now(),
              },
            }));
            setSelectedModel(findModelByRoute(payload.route.provider, payload.route.model));
            setIsModelConfirmed(true);
          }
          const canvasArtifact = isCanvasMode && payload.reply
            ? extractCanvasArtifactFromContent(payload.reply)
            : null;

          if (canvasArtifact?.code) {
            // Real HTML/JSX artifact — show explanation text + artifact card, render in Canvas pane
            let code = canvasArtifact.code;
            let kind = canvasArtifact.kind || "html";

            // Bind the real Google Sheet data into the dashboard DETERMINISTICALLY.
            // We never trust the model to carry the data: it often writes a truncated
            // sample ending in a literal "..." (invalid JS) or ignores the placeholder.
            // Strategy: (1) fill __SHEET_DATA__, (2) force-replace ANY model-written
            // `const/let/var DATA = [ ... ];` with the real rows (repairs bad output),
            // (3) last resort expose a global, then (4) force HTML so a dashboard never
            // lands in the React/Sandpack sandbox.
            if (sheetDataRef.current) {
              try {
                const MAX_INJECT_ROWS = 2000;
                const rows = Array.isArray(sheetDataRef.current)
                  ? sheetDataRef.current.slice(0, MAX_INJECT_ROWS)
                  : [];
                const dataJson = JSON.stringify(rows);
                if (code.includes("__SHEET_DATA__")) {
                  code = code.replace(/__SHEET_DATA__/g, dataJson);
                }
                // Override/repair any DATA array literal the model emitted (handles "...").
                const dataDeclRe = /((?:const|let|var)\s+DATA\s*=\s*)\[[\s\S]*?\]\s*;/;
                if (dataDeclRe.test(code)) {
                  code = code.replace(dataDeclRe, `$1${dataJson};`);
                } else if (!code.includes(dataJson)) {
                  // Nothing bound yet — expose the rows as a global the dashboard can read.
                  const tag = `<script>window.__SHEET_DATA__=${dataJson};</script>`;
                  if (/<\/head>/i.test(code)) code = code.replace(/<\/head>/i, `${tag}</head>`);
                  else if (/<body[^>]*>/i.test(code)) code = code.replace(/(<body[^>]*>)/i, `$1${tag}`);
                }
                // A Google-Sheet dashboard is always a standalone HTML page.
                if (/(<!doctype\s+html|<html\b|<head\b|<body\b|<div\b|<canvas\b)/i.test(code)) {
                  kind = "html";
                }
              } catch {
                /* keep code as-is if serialization fails */
              }
              sheetDataRef.current = null;
            }

            // Keep only the explanation text (code removed) for the chat bubble
            const textContent = stripCanvasCodeFromText(payload.reply);

            // Extract <title> from HTML for the artifact card
            const titleMatch = code.match(/<title[^>]*>([^<]*)<\/title>/i);
            const canvasTitle = titleMatch?.[1]?.trim() || "ตัวอย่าง HTML CODE";

            setStoredCanvas({ code, kind });
            // Streaming finished — swap the live code for the final artifact preview
            liveCanvasStartedRef.current = false;
            setIsStreamingCanvas(false);
            setLiveCanvasCode("");
            // Auto-open the Canvas pane once the LLM finishes (Gemini-style)
            setIsCanvasPaneOpen(true);
            setCanvasViewMode("preview");
            setMessages((prev) =>
              prev.map((m) =>
                m.id === streamId
                  ? {
                      ...m,
                      content: textContent,
                      canvasCode: code,
                      canvasKind: kind,
                      canvasTitle,
                      canvasTimestamp: Date.now(),
                      citations: payload.citations || [],
                      tokens: payload.tokens || {},
                      memory: payload.memory || {},
                    }
                  : m
              )
            );
          } else {
            // No real artifact (e.g. step-by-step questions in Canvas mode).
            // Stop the live stream view and show the text in chat. Keep an
            // existing canvas open (only close if nothing was ever generated).
            liveCanvasStartedRef.current = false;
            setIsStreamingCanvas(false);
            setLiveCanvasCode("");
            if (isCanvasMode && !storedCanvas?.code) setIsCanvasPaneOpen(false);
            setMessages((prev) =>
              prev.map((m) =>
                m.id === streamId
                  ? {
                      ...m,
                      content: payload.reply || m.content || "(ไม่มีคำตอบ)",
                      citations: payload.citations || [],
                      tokens: payload.tokens || {},
                      memory: payload.memory || {},
                    }
                  : m
              )
            );
          }
        };

        // Frontend inactivity watchdog: if the stream goes silent for too long
        // (proxy stall, dropped connection), abort instead of hanging forever.
        // Must stay longer than the backend idle timeout so the backend's error
        // event surfaces first; heartbeats (every ~12s) keep this alive while the
        // model is actively generating.
        const FRONTEND_STREAM_IDLE_MS = 55000;
        const readChunk = async () => {
          let idleTimer = null;
          try {
            return await Promise.race([
              reader.read(),
              new Promise((_, reject) => {
                idleTimer = setTimeout(() => {
                  try {
                    abortController.abort();
                  } catch {
                    // ignore
                  }
                  const e = new Error("การเชื่อมต่อกับเซิร์ฟเวอร์เงียบนานเกินไป กรุณาลองใหม่");
                  e.code = "FRONTEND_STREAM_IDLE";
                  reject(e);
                }, FRONTEND_STREAM_IDLE_MS);
              }),
            ]);
          } finally {
            if (idleTimer) clearTimeout(idleTimer);
          }
        };

        while (true) {
          const { done, value } = await readChunk();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split("\n\n");
          buffer = events.pop() || "";

          for (const eventBlock of events) {
            const lines = eventBlock.split("\n").map((l) => l.trim()).filter(Boolean);
            if (!lines.length) continue;

            let eventName = "message";
            const dataLines = [];
            for (const line of lines) {
              if (line.startsWith(":")) continue; // ignore SSE comments
              if (line.startsWith("event:")) eventName = line.slice(6).trim();
              else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
            }

            if (!dataLines.length) continue;
            const dataRaw = dataLines.join("\n");
            let parsed = null;
            try {
              parsed = JSON.parse(dataRaw);
            } catch {
              continue;
            }

            if (eventName === "delta") {
              appendAssistantText(parsed.text || "");
            } else if (eventName === "status") {
              // Real progress signal: reference material was gathered for this turn.
              if (parsed?.stage === "sources") {
                setLiveSourceCount(Number(parsed.count || 0));
                setLlmStatusText("กำลังเรียบเรียงคำตอบให้คุณ...");
              }
            } else if (eventName === "done") {
              applyFinal(parsed);
            } else if (eventName === "error") {
              throw new Error(parsed?.message || "stream failed");
            }
          }
        }

        if (streamPaintTimer) {
          clearInterval(streamPaintTimer);
          streamPaintTimer = null;
        }
        flushAssistantText();

        if (!finalPayload) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === streamId
                ? {
                    ...m,
                    content: m.content || "(ไม่มีคำตอบ)",
                  }
                : m
            )
          );
        }
      } else {
        const data = await res.json();
        const reply = data.reply || "(ไม่มีคำตอบ)";
        setAwaitingFirstToken(false);
        if (data?.route?.provider && data?.route?.model) {
          maybeShowSwitchedNotice(data.route);
          setConversationLocks((prev) => ({
            ...prev,
            [chatId]: {
              provider: data.route.provider,
              model: data.route.model,
              lockedAt: prev?.[chatId]?.lockedAt || Date.now(),
            },
          }));
          setSelectedModel(findModelByRoute(data.route.provider, data.route.model));
          setIsModelConfirmed(true);
        }
        const assistantMsg = {
          role: "assistant",
          content: reply,
          citations: data.citations || [],
          tokens: data.tokens || {},
          memory: data.memory || {},
        };
        setMessages((prev) => [...prev, assistantMsg]);
      }

      await loadUsage();
      await loadConversations();
      if (attachments.length > 0) {
        setAttachments([]);
        setOcrStatusText("");
      }
      setOcrPageStateByFile({});
      setOcrProgress({
        active: false,
        phase: "",
        fileName: "",
        totalFiles: 0,
        doneFiles: 0,
        errorFiles: 0,
        donePages: 0,
        currentPage: 0,
        totalPages: 0,
        tokenPreview: "",
        tokensEstimate: 0,
        percent: 0,
      });
    } catch (e) {
      console.error("sendMessage error:", e);
      const isAbortError = e?.name === "AbortError" || /aborted|abort/i.test(e?.message || "");
      if (isAbortError) {
        setThinking(false);
        setAwaitingFirstToken(false);
        return;
      }
      setAwaitingFirstToken(false);
      const rawMessage = e?.message || "เกิดข้อผิดพลาด";
      const displayMessage = getChatErrorMessageForUser(rawMessage);
      const isQuotaError = /โควต้าของโมเดลขั้นสูงหมดแล้ว/.test(displayMessage);
      toast({
        variant: "error",
        title: "ส่งข้อความไม่สำเร็จ",
        message: displayMessage,
        duration: isQuotaError ? 4200 : 3000,
      });
      setAttachments((prev) =>
        prev.map((x) => {
          if (x.state === "processing" || x.state === "uploaded") {
            return { ...x, state: "error" };
          }
          return x;
        })
      );
      setOcrStatusText("การประมวลผลไฟล์ล้มเหลว กรุณาลองใหม่");
      setOcrProgress((prev) => ({
        ...prev,
        active: true,
        phase: "error",
        errorFiles: Math.max(1, prev.errorFiles || 0),
      }));
    } finally {
      abortControllerRef.current = null;
      setThinking(false);
      setAwaitingFirstToken(false);
      setLlmStatusText("");
      // Make sure the live-canvas streaming view never gets stuck on
      liveCanvasStartedRef.current = false;
      setIsStreamingCanvas(false);
      // Keep composerTool (Canvas mode) sticky across turns — the user exits
      // Canvas by clicking the X on the chip. Clearing it here previously made
      // follow-ups fall back to default mode (code leaked into chat + low token
      // limit truncated the new code).
    }
  };

  const handlePickFiles = (files) => {
    const acceptedByType = files.filter((f) => {
      const n = (f.name || "").toLowerCase();
      return ALLOWED_ATTACHMENT_EXTENSIONS.some((ext) => n.endsWith(ext));
    });
    if (acceptedByType.length === 0) {
      toast({
        variant: "error",
        title: "แนบไฟล์ไม่สำเร็จ",
        message: "รองรับเฉพาะไฟล์ .pdf, .docx และ Excel (.xlsx/.xls)",
        duration: 2200,
      });
      return;
    }
    const oversizeFiles = acceptedByType.filter(
      (f) => Number(f.size || 0) > MAX_ATTACHMENT_SIZE_BYTES
    );
    const accepted = acceptedByType.filter(
      (f) => Number(f.size || 0) <= MAX_ATTACHMENT_SIZE_BYTES
    );
    if (oversizeFiles.length > 0) {
      const sizeMb = (MAX_ATTACHMENT_SIZE_BYTES / (1024 * 1024)).toFixed(0);
      const sampleNames = oversizeFiles.slice(0, 2).map((f) => f.name).join(", ");
      const moreCount = Math.max(0, oversizeFiles.length - 2);
      const suffix = moreCount > 0 ? ` และอีก ${moreCount} ไฟล์` : "";
      toast({
        variant: "error",
        title: "ไฟล์ใหญ่เกินกำหนด",
        message: `${sampleNames}${suffix} ต้องมีขนาดไม่เกิน ${sizeMb}MB ต่อไฟล์`,
        duration: 3200,
      });
    }
    if (accepted.length === 0) {
      return;
    }

    const toUpload = [];
    setAttachments((prev) => {
      const seen = new Set(prev.map((x) => x.signature));
      const next = [...prev];
      if (next.length >= 3) {
        return next;
      }
      for (const f of accepted) {
        if (next.length >= 3) break;
        const signature = `${f.name}|${f.size}|${f.lastModified}`;
        if (seen.has(signature)) continue;
        seen.add(signature);
        const att = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          name: f.name,
          size: f.size,
          signature,
          file: f,
          state: "selected",
          ocrText: "",
          fileId: "",
          uploadProgress: 0,
          progress: { totalPages: 0, donePages: 0, tokensEstimate: 0 },
        };
        next.push(att);
        toUpload.push(att);
      }
      return next;
    });

    const currentCount = attachments.length;
    if (currentCount >= 3) {
      toast({
        variant: "error",
        title: "แนบไฟล์ได้สูงสุด 3 ไฟล์",
        message: "ลบไฟล์เดิมออกก่อนแนบไฟล์ใหม่",
        duration: 2200,
      });
      return;
    }

    if (currentCount + toUpload.length > 3) {
      toast({
        variant: "error",
        title: "แนบไฟล์ได้สูงสุด 3 ไฟล์",
        message: "ระบบรับเฉพาะ 3 ไฟล์แรกจากชุดที่เลือก",
        duration: 2200,
      });
    }

    const startUpload = async (attachment) => {
      const ctrl = new AbortController();
      uploadControllersRef.current.set(attachment.id, ctrl);
      let simulated = 20;
      const timer = setInterval(() => {
        simulated = Math.min(simulated + 8, 90);
        setAttachments((prev) =>
          prev.map((x) =>
            x.id === attachment.id && x.state === "uploading"
              ? { ...x, uploadProgress: simulated }
              : x
          )
        );
      }, 250);
      try {
        setAttachments((prev) =>
          prev.map((x) =>
            x.id === attachment.id
              ? {
                  ...x,
                  state: "uploading",
                  uploadProgress: 20,
                }
              : x
          )
        );
        const fd = new FormData();
        fd.append("file", attachment.file, attachment.name);
        setOcrStatusText(`กำลังอัปโหลดไฟล์ ${attachment.name}...`);
        const uploadRes = await fetch(`${API_BASE}/ocr/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${lsGet("token")}` },
          body: fd,
          signal: ctrl.signal,
        });
        const uploadData = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok || !uploadData?.file_id) {
          const serverMessage =
            uploadData?.message || uploadData?.detail || uploadData?.error || "";
          throw new Error(
            getUploadErrorMessageForUser(uploadRes.status, serverMessage)
          );
        }

        setAttachments((prev) =>
          prev.map((x) =>
            x.id === attachment.id
              ? {
                  ...x,
                  fileId: uploadData.file_id,
                  state: "uploaded",
                  uploadProgress: 100,
                }
              : x
          )
        );
        setOcrStatusText(`แนบไฟล์แล้ว: ${attachment.name}`);
      } catch (e) {
        const isAbortError = e?.name === "AbortError" || /aborted|abort/i.test(e?.message || "");
        if (isAbortError) {
          setAttachments((prev) => prev.filter((x) => x.id !== attachment.id));
        } else {
          setAttachments((prev) =>
            prev.map((x) => {
              if (x.id !== attachment.id) return x;
              return {
                ...x,
                state: "error",
                uploadProgress: x.uploadProgress || 0,
              };
            })
          );
        }
        if (!isAbortError) {
          toast({
            variant: "error",
            title: "อัปโหลดไฟล์ไม่สำเร็จ",
            message: e?.message || `อัปโหลด ${attachment.name} ล้มเหลว`,
            duration: 2600,
          });
        }
      } finally {
        clearInterval(timer);
        uploadControllersRef.current.delete(attachment.id);
      }
    };

    toUpload.forEach((attachment) => startUpload(attachment));
  };

  const handleRemoveAttachment = (id) => {
    const ctl = uploadControllersRef.current.get(id);
    if (ctl) {
      try {
        ctl.abort();
      } catch {
        // ignore
      }
      uploadControllersRef.current.delete(id);
    }
    setAttachments((prev) => {
      const next = prev.filter((x) => x.id !== id);
      if (next.length === 0) setOcrStatusText("");
      return next;
    });
  };

  const handleCanvasAction = (tool = "canvas") => {
    if (thinking || isSendingRef.current) return;
    // ไม่ auto-open pane ตอนเลือก canvas — จะเปิดเองตอน AI ตอบกลับ
    setComposerTool(tool);
  };

  const clearComposerTool = () => {
    setComposerTool("");
    // ปิด pane เฉพาะถ้ายังไม่มี canvas content
    if (!storedCanvas?.code) setIsCanvasPaneOpen(false);
  };

  // Send a ready-made inspiration mega prompt straight to the LLM (Canvas mode).
  const handleSendInspiration = (prompt) => {
    if (thinking || isSendingRef.current) return;
    if (typeof prompt !== "string" || !prompt.trim()) return;
    setComposerTool(COMPOSER_TOOL_CANVAS);
    sendMessage(prompt);
  };

  // Inspiration chip click — if it needs a data source (Google Sheet), open the modal.
  const handleInspirationClick = (item) => {
    if (thinking || isSendingRef.current) return;
    if (item?.requiresSheet) {
      sheetPromptRef.current = item.prompt || "";
      setSheetUrl("");
      setSheetError("");
      setSheetModalOpen(true);
      return;
    }
    handleSendInspiration(item?.prompt);
  };

  // Build a compact schema block (columns + types + a few sample rows) for the LLM.
  // Keep it small + bounded so it never blows past the canvas input limit (which
  // would silently truncate the tail). The __SHEET_DATA__ rule is placed FIRST so
  // it always survives any truncation.
  const buildSheetSchemaText = (data) => {
    const columns = Array.isArray(data?.columns) ? data.columns : [];
    const cols = columns.map((c) => `${c.name} (${c.type})`).join(", ");
    // Bound the sample: few rows + clipped cell values, then cap the JSON length.
    const rawSample = Array.isArray(data?.sample) ? data.sample.slice(0, 6) : [];
    const clippedSample = rawSample.map((row) => {
      const o = {};
      Object.keys(row || {}).forEach((k) => {
        const v = row[k];
        o[k] = typeof v === "string" && v.length > 60 ? `${v.slice(0, 60)}…` : v;
      });
      return o;
    });
    let sampleJson = JSON.stringify(clippedSample);
    if (sampleJson.length > 2500) sampleJson = `${sampleJson.slice(0, 2500)}…`;
    return [
      "DATA SCHEMA (ระบบดึงข้อมูลจาก Google Sheet มาให้แล้ว — ห้ามถามหาลิงก์):",
      "สำคัญ: เมื่อถึงขั้นเจนโค้ด ให้เจนไฟล์ HTML เดียว (ห้าม React/JSX) และประกาศ const DATA = __SHEET_DATA__;  (ห้ามแก้/ลบ placeholder __SHEET_DATA__ ระบบจะแทนข้อมูลจริงให้ ห้ามเขียนข้อมูลตัวอย่างเองหรือใส่ ... จุดไข่ปลา) และห้าม hardcode ตัวเลข ให้คำนวณทุกค่าจาก DATA",
      `- จำนวนแถวทั้งหมด: ${data?.rowCount ?? 0}${data?.truncated ? " (ตัดมาบางส่วน)" : ""}`,
      `- คอลัมน์ (${columns.length}): ${cols}`,
      `- ตัวอย่างข้อมูล (JSON ${clippedSample.length} แถวแรก): ${sampleJson}`,
    ].join("\n");
  };

  // Fetch the sheet via backend proxy, then fire off the dashboard mega prompt.
  const submitSheetUrl = async () => {
    const url = sheetUrl.trim();
    if (!url) {
      setSheetError("กรุณาวางลิงก์ Google Sheet ก่อน");
      return;
    }
    if (thinking || isSendingRef.current) return;
    setSheetLoading(true);
    setSheetError("");
    try {
      const res = await fetch(`${API_BASE}/canvas/sheet`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${lsGet("token")}`,
        },
        body: JSON.stringify({ url }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        setSheetError(data?.message || "อ่าน Sheet ไม่สำเร็จ กรุณาตรวจสอบลิงก์และสิทธิ์การเข้าถึง");
        setSheetLoading(false);
        return;
      }
      sheetDataRef.current = Array.isArray(data.rows) ? data.rows : [];
      const base = sheetPromptRef.current || "";
      const fullPrompt = `${base}\n\n${buildSheetSchemaText(data)}`;
      setSheetLoading(false);
      setSheetModalOpen(false);
      setSheetUrl("");
      setComposerTool(COMPOSER_TOOL_CANVAS);
      sendMessage(fullPrompt);
    } catch (e) {
      setSheetError("เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
      setSheetLoading(false);
    }
  };

  const dismissCanvasPane = () => {
    setIsCanvasPaneOpen(false);
    setCanvasViewMode("preview");
    setComposerTool("");
  };

  const reopenCanvasPane = () => {
    setIsCanvasPaneOpen(true);
    setCanvasViewMode("preview");
    setComposerTool(COMPOSER_TOOL_CANVAS);
  };

  /* =========================================================
       EFFECTS
    ========================================================== */
  useEffect(() => {
    loadConversations();
    loadUsage();
    loadAdminAccess();
  }, []);

  useEffect(() => {
    if (!isAdmin && activeMenu === "overview") {
      setActiveMenu("chat");
    }
  }, [isAdmin, activeMenu]);

  useEffect(() => {
    try {
      localStorage.setItem(
        DESKTOP_SIDEBAR_COLLAPSE_STORAGE_KEY,
        isDesktopSidebarCollapsed ? "1" : "0"
      );
    } catch {
      // ignore persistence errors
    }
  }, [isDesktopSidebarCollapsed]);

  useEffect(() => {
    // Reset canvas pane visibility on conversation change, but keep composerTool
    // (Canvas mode) intact — navigating to a freshly created /c/<cid> must NOT
    // drop Canvas mode, otherwise follow-up turns leak code into the chat and
    // trigger a render loop. Canvas mode is cleared only via the chip X or the
    // explicit "new chat" action.
    setStoredCanvas({ code: "", kind: "html" });
    setIsCanvasPaneOpen(false);
    if (cid) {
      setActiveChat({ id: cid, cid });
      setIsModelConfirmed(false);
      if (!isSendingRef.current) {
        loadChatMessages(cid);
      }
    } else {
      setActiveChat(null);
      setMessages([]);
      setIsModelConfirmed(false);
      shouldStickToBottomRef.current = true;
    }
  }, [cid]);

  useEffect(() => {
    if (!shouldStickToBottomRef.current && !thinking) return;
    const behavior = thinking ? "auto" : "smooth";
    const id = requestAnimationFrame(() => {
      scrollToBottom(behavior);
      chatEndRef.current?.scrollIntoView({ behavior });
    });
    return () => cancelAnimationFrame(id);
  }, [messages, thinking]);

  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [cid]);

  /* =========================================================
       FILE HANDLERS
    ========================================================== */
  // Delete chat
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteChat = async (cid) => {
    // ✅ แสดง modal ก่อน
    const chat = chatList.find((c) => c.cid === cid || c.id === cid);
    setDeleteTarget(chat);
  };

  const confirmDelete = async () => {
    const cid = deleteTarget?.cid || deleteTarget?.id;
    setDeleteTarget(null); // ปิด modal

    try {
      const res = await fetch(`${API_BASE}/conversation/${cid}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${lsGet("token")}` },
      });
      const data = await res.json();

      if (res.ok) {
        setChatList((prev) =>
          prev.filter((c) => c.cid !== cid && c.id !== cid)
        );
        if (activeChat?.cid === cid) {
          setActiveChat(null);
          setMessages([]);
          navigate("/");
        }

        toast({
          variant: "success",
          title: "ลบสำเร็จ",
          message: "ประวัติการสนทนาถูกลบออกจากระบบแล้ว",
          duration: 2000,
        });
      } else {
        toast({
          variant: "error",
          title: "ไม่สามารถลบได้",
          message: data.error || "เกิดข้อผิดพลาดในการลบแชท",
          duration: 2500,
        });
      }
    } catch (e) {
      console.error("delete chat error:", e);
      toast({
        variant: "error",
        title: "ล้มเหลว",
        message: "เกิดข้อผิดพลาดระหว่างการลบแชท",
        duration: 2500,
      });
    }
  };

  // Rename chat
  const handleRenameChat = async (cid, newTitle) => {
    if (!newTitle || !newTitle.trim()) return;
    try {
      const res = await fetch(`${API_BASE}/conversation/${cid}/title`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${lsGet("token")}`,
        },
        body: JSON.stringify({ title: newTitle.trim() }),
      });

      const data = await res.json();
      if (res.ok) {
        setChatList((prev) =>
          prev.map((c) =>
            c.cid === cid || c.id === cid ? { ...c, title: newTitle.trim() } : c
          )
        );
        loadConversations(); // รีเฟรช background
        if (activeChat?.cid === cid) {
          setActiveChat((prev) => ({ ...prev, title: newTitle.trim() }));
        }

        toast({
          variant: "success",
          title: "เปลี่ยนชื่อสำเร็จ",
          message: "ชื่อบทสนทนาได้รับการอัปเดตแล้ว",
          duration: 2000,
        });
      } else {
        toast({
          variant: "error",
          title: "ไม่สามารถเปลี่ยนชื่อได้",
          message: data.error || "เกิดข้อผิดพลาดในการเปลี่ยนชื่อ",
          duration: 2500,
        });
      }
    } catch (e) {
      console.error("rename chat error:", e);
      toast({
        variant: "error",
        title: "ล้มเหลว",
        message: "เกิดข้อผิดพลาดระหว่างการเปลี่ยนชื่อบทสนทนา",
        duration: 2500,
      });
    }
  };

  /* =========================================================
       UI
    ========================================================== */
  const isAwaitingAssistant = thinking && awaitingFirstToken;
  const activeLock = cid ? conversationLocks[cid] : null;
  const requiresModelSelection =
    !activeLock && !isModelConfirmed && messages.length === 0;
  const llmWaitHint = (() => {
    // Once reference material is actually gathered, show the concrete count
    // (honest, specific progress) — without naming any particular data source.
    if (liveSourceCount > 0) {
      return `อ้างอิงจากข้อมูล ${liveSourceCount} แหล่ง • กำลังเรียบเรียงคำตอบ...`;
    }
    // Advance a step every ~3s so the user sees continuous movement, not a freeze.
    return LLM_WAIT_HINTS[
      Math.min(LLM_WAIT_HINTS.length - 1, Math.floor(llmWaitingSeconds / 3))
    ];
  })();
  const latestCanvasPayload = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i];
      if (msg?.role !== "assistant") continue;
      // New format: canvasCode stored directly on the message object
      if (msg?.canvasCode) {
        return {
          kind: msg.canvasKind || "html",
          language: msg.canvasKind || "html",
          code: msg.canvasCode,
          messageIndex: i,
        };
      }
      // Skip content-extraction for a message that is still streaming — its
      // content grows each chunk and would otherwise rebuild the preview blob
      // on every token (causing a render loop). The finished artifact is set
      // via canvasCode in applyFinal.
      if (typeof msg?.id === "string" && msg.id.startsWith("stream-")) continue;
      // Legacy format: extract HTML from message content (old conversations)
      const artifact = extractCanvasArtifactFromContent(msg?.content || "");
      if (!artifact) continue;
      return { ...artifact, messageIndex: i };
    }
    return null;
  }, [messages]);
  const [canvasViewMode, setCanvasViewMode] = useState("preview");
  const [isCanvasPaneOpen, setIsCanvasPaneOpen] = useState(false);
  // Live code streaming into the Code tab while the AI writes (progress feedback)
  const [liveCanvasCode, setLiveCanvasCode] = useState("");
  const [isStreamingCanvas, setIsStreamingCanvas] = useState(false);
  const liveCanvasStartedRef = useRef(false);
  const liveCanvasBufRef = useRef("");
  const lastLivePaintRef = useRef(0);
  const canvasCodeStartRef = useRef(-1);
  // storedCanvas captures the code directly from stream done event — reliable fallback
  const [storedCanvas, setStoredCanvas] = useState({ code: "", kind: "html" });
  const canvasArtifactCode = latestCanvasPayload?.code || storedCanvas.code;
  const canvasArtifactKind = latestCanvasPayload?.kind || storedCanvas.kind;
  const isReactCanvasArtifact = canvasArtifactKind === "react";
  const hasEffectiveCanvas = Boolean(canvasArtifactCode);

  // Blob URL for canvas preview — gives isolated origin so localStorage works
  // without exposing the parent app's data (Gemini/Claude standard approach).
  const [canvasBlobUrl, setCanvasBlobUrl] = useState(null);
  // Loading overlay shown while the app boots inside the iframe (Gemini-style)
  const [canvasLoading, setCanvasLoading] = useState(false);
  const canvasLoadingTimerRef = useRef(null);
  useEffect(() => {
    if (!canvasArtifactCode) {
      setCanvasBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
      return;
    }
    const html = injectLinkHandler(fixTailwindOrder(canvasArtifactCode));
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    setCanvasBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    return () => URL.revokeObjectURL(url);
  }, [canvasArtifactCode]);

  // Show the loading overlay whenever the pane opens with a fresh blob URL
  useEffect(() => {
    if (isCanvasPaneOpen && canvasBlobUrl && canvasViewMode === "preview") {
      setCanvasLoading(true);
      if (canvasLoadingTimerRef.current) clearTimeout(canvasLoadingTimerRef.current);
      // Safety fallback: hide overlay after 6s even if onLoad never fires
      canvasLoadingTimerRef.current = setTimeout(() => setCanvasLoading(false), 6000);
    }
    return () => {
      if (canvasLoadingTimerRef.current) clearTimeout(canvasLoadingTimerRef.current);
    };
  }, [isCanvasPaneOpen, canvasBlobUrl, canvasViewMode]);

  const handleCanvasIframeLoad = () => {
    // Keep the overlay visible a touch longer for a smooth boot animation
    if (canvasLoadingTimerRef.current) clearTimeout(canvasLoadingTimerRef.current);
    canvasLoadingTimerRef.current = setTimeout(() => setCanvasLoading(false), 650);
  };

  // Download the current canvas artifact as a standalone file
  const handleCanvasDownload = () => {
    if (!canvasArtifactCode) return;
    const isReact = canvasArtifactKind === "react";
    const ext = isReact ? "jsx" : "html";
    const mime = isReact ? "text/jsx;charset=utf-8" : "text/html;charset=utf-8";
    const content = isReact
      ? canvasArtifactCode
      : fixTailwindOrder(canvasArtifactCode);
    const titleMatch = canvasArtifactCode.match(/<title[^>]*>([^<]*)<\/title>/i);
    const rawName = titleMatch?.[1]?.trim() || "canvas";
    const safeName =
      String(rawName)
        .trim()
        .replace(/[\\/:*?"<>|]+/g, "")
        .replace(/\s+/g, "-")
        .slice(0, 60) || "canvas";
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeName}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast({
      variant: "success",
      title: "ดาวน์โหลดสำเร็จ",
      message: `บันทึกไฟล์ ${safeName}.${ext} แล้ว`,
      duration: 2200,
    });
  };

  // Split view stays active while Canvas pane is open.
  const isCanvasRendering = thinking && (composerTool === COMPOSER_TOOL_CANVAS || hasEffectiveCanvas);
  const shouldShowCanvasPane = isCanvasPaneOpen;
  const inputActiveTool =
    shouldShowCanvasPane || composerTool === COMPOSER_TOOL_CANVAS
      ? COMPOSER_TOOL_CANVAS
      : "";

  // Canvas pane opens only when user clicks "เปิด" on the artifact card (Gemini-style)
  // No auto-open effect.

  const computedOcrPercent = (() => {
    const filePart =
      ocrProgress.totalFiles > 0 ? ocrProgress.doneFiles / ocrProgress.totalFiles : 0;
    const pagePart =
      ocrProgress.totalPages > 0 ? ocrProgress.donePages / ocrProgress.totalPages : 0;
    const combined = Math.min(1, Math.max(filePart, pagePart));
    const fromEvent = Number(ocrProgress.percent || 0) / 100;
    return Math.round(Math.max(combined, fromEvent) * 100);
  })();

  return (
    <div className="flex h-screen overflow-hidden bg-[#f8fafc] text-slate-800 font-sans">
      {/* NAVBAR */}
      <nav className="hidden md:flex w-[84px] bg-white/80 backdrop-blur-2xl border-r border-slate-200/50 flex-col items-center py-6 z-20 shadow-[8px_0_30px_rgba(0,0,0,0.04)]">
        {/* Logo */}
        <div className="mb-8 relative group cursor-pointer">
          <div className="absolute -inset-3 rounded-3xl bg-gradient-to-tr from-blue-500/40 via-violet-500/40 to-cyan-400/40 blur-xl group-hover:blur-2xl transition-all duration-500 opacity-70 group-hover:opacity-100" />
          <div className="relative w-14 h-14 rounded-2xl bg-white flex items-center justify-center border border-white/60 shadow-xl shadow-blue-900/10 overflow-hidden transform group-hover:scale-105 transition-all duration-500">
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              className="w-full h-full object-contain bg-slate-50"
              aria-label="PSRU AI Hub avatar"
            >
              <source src={`${process.env.PUBLIC_URL}/avatar2.mp4`} type="video/mp4" />
            </video>
          </div>
        </div>

        {/* Menu Icons */}
        <div className="flex flex-col items-center gap-4 w-full px-3">
          <IconButton
            icon={<MessageSquare size={20} />}
            active={activeMenu === "chat"}
            onClick={() => setActiveMenu("chat")}
          />
          {isAdmin && (
            <IconButton
              icon={<PieChart size={20} />}
              active={activeMenu === "overview"}
              onClick={() => {
                setActiveMenu("overview");
                loadUsage();
              }}
            />
          )}
          <IconButton
            icon={<BarChart3 size={20} />}
            active={activeMenu === "usage"}
            onClick={() => {
              setActiveMenu("usage");
              loadUsage();
            }}
          />
          <IconButton
            icon={<Lightbulb size={20} />}
            active={activeMenu === "feedback"}
            onClick={() => setActiveMenu("feedback")}
          />
        </div>

        {/* Profile Icon */}
        <div className="mt-auto mb-4">
          <IconButton
            icon={<UserCircle size={22} />}
            active={activeMenu === "profile"}
            onClick={() => setActiveMenu("profile")}
          />
        </div>
      </nav>

      {/* SIDEBAR */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-40 w-[85vw] max-w-[320px] bg-[#f8fafc] border-r border-slate-200/80 flex flex-col transition-all duration-300 ease-out shadow-2xl md:shadow-none
        ${mobileSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        ${isDesktopSidebarCollapsed ? "md:w-0 md:min-w-0 md:border-r-0 md:overflow-hidden md:opacity-0 md:pointer-events-none" : "md:w-[340px] md:opacity-100"}`}
      >
        {/* Header */}
        <div className="px-6 py-7 border-b border-slate-200/50 bg-transparent sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-2xl font-extrabold tracking-tight flex items-center gap-2 leading-snug">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-cyan-500 py-0.5 inline-block">
                  PSRU AI Hub
                </span>
              </h2>
              <p className="text-[9px] font-bold text-slate-400 tracking-widest uppercase mt-0.5">
                Pibulsongkram Rajabhat Univ.
              </p>
            </div>
          </div>

          <div className={`lg:hidden mt-6 bg-slate-100/80 p-1.5 rounded-2xl grid ${isAdmin ? 'grid-cols-5' : 'grid-cols-4'} gap-1 shadow-inner`}>
            <button
              onClick={() => setActiveMenu("chat")}
              className={`h-12 rounded-xl text-[10px] font-semibold transition-all duration-300 flex flex-col items-center justify-center gap-1 ${
                activeMenu === "chat"
                  ? "bg-white text-blue-600 shadow-sm shadow-slate-200/50 scale-100"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 scale-95 hover:scale-100"
              }`}
            >
              <MessageSquare size={16} />
              แชท
            </button>
            {isAdmin && (
              <button
                onClick={() => {
                  setActiveMenu("overview");
                  loadUsage();
                }}
                className={`h-12 rounded-xl text-[10px] font-semibold transition-all duration-300 flex flex-col items-center justify-center gap-1 ${
                  activeMenu === "overview"
                    ? "bg-white text-violet-600 shadow-sm shadow-slate-200/50 scale-100"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 scale-95 hover:scale-100"
                }`}
              >
                <PieChart size={16} />
                ภาพรวม
              </button>
            )}
            <button
              onClick={() => {
                setActiveMenu("usage");
                loadUsage();
              }}
              className={`h-12 rounded-xl text-[10px] font-semibold transition-all duration-300 flex flex-col items-center justify-center gap-1 ${
                activeMenu === "usage"
                  ? "bg-white text-cyan-600 shadow-sm shadow-slate-200/50 scale-100"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 scale-95 hover:scale-100"
              }`}
            >
              <BarChart3 size={16} />
              โควต้า
            </button>
            <button
              onClick={() => setActiveMenu("feedback")}
              className={`h-12 rounded-xl text-[10px] font-semibold transition-all duration-300 flex flex-col items-center justify-center gap-1 ${
                activeMenu === "feedback"
                  ? "bg-white text-amber-600 shadow-sm shadow-slate-200/50 scale-100"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 scale-95 hover:scale-100"
              }`}
            >
              <Lightbulb size={16} />
              เสนอแนะ
            </button>
            <button
              onClick={() => setActiveMenu("profile")}
              className={`h-12 rounded-xl text-[10px] font-semibold transition-all duration-300 flex flex-col items-center justify-center gap-1 ${
                activeMenu === "profile"
                  ? "bg-white text-emerald-600 shadow-sm shadow-slate-200/50 scale-100"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 scale-95 hover:scale-100"
              }`}
            >
              <UserCircle size={16} />
              โปรไฟล์
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 custom-scroll">
          {activeMenu === "chat" && (
            <ChatHistoryPanel
              chatList={chatList}
              search={search}
              setSearch={setSearch}
              setActiveChat={setActiveChat}
              activeChat={activeChat}
              navigate={navigate}
              menuOpen={menuOpen}
              setMenuOpen={setMenuOpen}
              handleCreateNewChat={() => handleCreateNewChat(selectedModel)}
              handleDeleteChat={handleDeleteChat}
              handleRenameChat={handleRenameChat}
            />
          )}

          {isAdmin && activeMenu === "overview" && <UsageOverviewPanel usage={usage} />}

          {activeMenu === "usage" && <UsagePanel usage={usage} />}

          {activeMenu === "feedback" && <FeedbackPanel />}

          {activeMenu === "notify" && (
            <NotifyPanel
              notifications={notifications}
              markRead={(id) =>
                setNotifications((prev) =>
                  prev.map((n) => (n.id === id ? { ...n, read: true } : n))
                )
              }
              deleteNotify={(id) =>
                setNotifications((prev) => prev.filter((n) => n.id !== id))
              }
            />
          )}

          {activeMenu === "settings" && (
            <SettingsPanel
              settings={activeSettings}
              updateSettings={(k, v) => updateSetting(selectedModel.name, k, v)}
              updateReasoning={(k, v) =>
                updateReasoning(selectedModel.name, k, v)
              }
              resetSettings={() => resetSettings(selectedModel.name)}
              responseMode={responseMode}
              setResponseMode={setResponseMode}
            />
          )}

          {activeMenu === "profile" && <ProfilePanel />}
        </div>
      </aside>
      <button
        type="button"
        onClick={() => setIsDesktopSidebarCollapsed((prev) => !prev)}
        className={`hidden md:flex fixed top-1/2 -translate-y-1/2 z-30 h-12 w-6 rounded-r-xl border border-slate-200/90 bg-white/95 text-slate-600 shadow-md hover:bg-white hover:text-slate-800 transition-all duration-300 items-center justify-center ${
          isDesktopSidebarCollapsed
            ? "left-[84px]"
            : "left-[424px] -translate-x-full"
        }`}
        title={isDesktopSidebarCollapsed ? "ขยายเมนูซ้าย" : "ย่อเมนูซ้าย"}
        aria-label={isDesktopSidebarCollapsed ? "ขยายเมนูซ้าย" : "ย่อเมนูซ้าย"}
      >
        {isDesktopSidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>

      {mobileSidebarOpen && (
        <button
          aria-label="Close sidebar backdrop"
          className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-30 md:hidden transition-opacity duration-300"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* CHAT MAIN */}
      <main className="min-w-0 flex-1 flex flex-col bg-[#fdfdfd] relative">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-gradient-to-br from-blue-400/10 to-violet-400/10 rounded-full blur-[120px] animate-pulse-slow"></div>
            <div className="absolute bottom-[-10%] left-[-10%] w-[700px] h-[700px] bg-gradient-to-tr from-cyan-400/10 to-blue-400/10 rounded-full blur-[130px] animate-pulse-slow delay-1000"></div>
        </div>

        <header className="sticky top-0 md:hidden flex items-center px-4 py-4 z-30 bg-white/70 backdrop-blur-xl border-b border-slate-200/50">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileSidebarOpen((v) => !v)}
              className="h-10 w-10 rounded-xl bg-white border border-slate-200 text-slate-600 flex items-center justify-center shadow-sm active:scale-95 transition-transform"
              aria-label="Toggle sidebar"
            >
              {mobileSidebarOpen ? <X size={20} /> : <PanelLeft size={20} />}
            </button>
            <h1 className="text-xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-cyan-500 py-0.5 leading-snug">
              PSRU AI Hub
            </h1>
          </div>
        </header>

        <div className="relative z-10 flex-1 min-h-0 flex">
          <section
            className={`min-w-0 flex flex-col transition-all duration-300 ${
              shouldShowCanvasPane
                ? "w-full lg:w-[38%] lg:max-w-[560px] bg-white/75 lg:border-r lg:border-slate-200/80"
                : "flex-1"
            }`}
          >
            <div
              ref={chatScrollRef}
              onScroll={handleChatScroll}
              className="flex-1 overflow-y-auto custom-scroll"
            >
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-start h-full px-6 pt-12 md:pt-16 pb-24 md:pb-28 [@media(max-height:900px)]:pt-7 [@media(max-height:900px)]:pb-40 animate-fadeIn">
              <div className="w-full max-w-3xl text-center space-y-8 [@media(max-height:900px)]:space-y-5">
                
                {/* Hero Icon */}
                <div className="relative inline-block group">
                    <div className="absolute -inset-4 bg-gradient-to-tr from-blue-500/30 via-violet-500/30 to-cyan-500/30 blur-2xl rounded-full group-hover:blur-3xl transition-all duration-700"></div>
                    <div className="relative w-24 h-24 [@media(max-height:900px)]:w-16 [@media(max-height:900px)]:h-16 bg-white rounded-3xl shadow-2xl shadow-blue-900/10 flex items-center justify-center border border-white/60 overflow-hidden transform group-hover:scale-105 transition-all duration-500">
                        <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-cover"
                        aria-label="PSRU AI Hub avatar"
                        >
                        <source src={`${process.env.PUBLIC_URL}/avatar2.mp4`} type="video/mp4" />
                        </video>
                    </div>
                </div>

                <div className="space-y-2">
                    <h1 className="text-4xl md:text-5xl [@media(max-height:900px)]:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-slate-800 via-blue-800 to-slate-800 tracking-tight py-2 leading-snug">
                        สวัสดีค่ะ, มีอะไรให้ช่วยไหมคะ?
                    </h1>
                    <p className="text-slate-500 text-base md:text-lg [@media(max-height:900px)]:text-sm font-medium">
                        ผู้ช่วยอัจฉริยะของคุณ พร้อมตอบคำถามและสร้างสรรค์เนื้อหา
                    </p>
                </div>

                {!requiresModelSelection && composerTool !== COMPOSER_TOOL_CANVAS && (
                  <div className="w-full max-w-4xl mx-auto pt-4 [@media(max-height:900px)]:pt-2 space-y-4 [@media(max-height:900px)]:space-y-3">
                      <div className="rounded-2xl border border-slate-200/80 bg-white/85 px-4 py-3 shadow-sm">
                        <p className="text-sm font-semibold text-slate-700">ไกด์เริ่มต้นใช้งาน</p>
                        <p className="mt-1 text-xs md:text-sm text-slate-500">
                          เลือกหัวข้อด้านล่างเพื่อเติมข้อความให้อัตโนมัติ แล้วกดส่งได้เลย
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 [@media(max-height:900px)]:gap-3">
                        {[
                            { icon: Sparkles, text: "ช่วยคิดแคปชั่นขายของออนไลน์" },
                            { icon: FileText, text: "สรุปบทความวิชาการให้หน่อย" },
                            { icon: BrainCircuit, text: "เขียนโค้ด React เชื่อมต่อ API" },
                            { icon: Lightbulb, text: "แนะนำไอเดียออกแบบโลโก้" }
                        ].map((item, idx) => (
                            <button
                                key={idx}
                                onClick={() => {
                                    setInput(item.text);
                                }}
                                className="flex items-center gap-4 p-5 [@media(max-height:900px)]:p-3.5 bg-white/75 backdrop-blur-sm border border-slate-200/70 rounded-2xl shadow-sm hover:shadow-md hover:border-blue-300 hover:bg-gradient-to-br hover:from-white hover:to-blue-50/50 transition-all duration-300 text-left group hover:-translate-y-1 min-h-[84px] [@media(max-height:900px)]:min-h-[68px]"
                            >
                                <span className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 group-hover:bg-blue-100 group-hover:text-blue-600 flex items-center justify-center transition-all duration-200">
                                  <item.icon size={18} strokeWidth={2.1} />
                                </span>
                                <span className="text-sm md:text-[15px] font-medium text-slate-600 group-hover:text-blue-700">{item.text}</span>
                            </button>
                        ))}
                      </div>
                  </div>
                )}
                {!requiresModelSelection && composerTool === COMPOSER_TOOL_CANVAS && (
                  <div className="w-full max-w-4xl mx-auto pt-4 space-y-4">
                      <div className="rounded-2xl border border-violet-200/80 bg-gradient-to-br from-violet-50/90 to-blue-50/90 px-4 py-3 shadow-sm">
                        <p className="text-sm font-semibold text-slate-700 flex items-center justify-center gap-2">
                          <Sparkles size={15} className="text-violet-500" />
                          แรงบันดาลใจสำหรับ Canvas
                        </p>
                        <p className="mt-1 text-xs md:text-sm text-slate-500">
                          เลือกหัวข้อด้านล่างเพื่อให้ AI พาสร้างชิ้นงาน หรือพิมพ์โจทย์ของคุณเองก็ได้
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {CANVAS_INSPIRATIONS.map((item) => {
                          const Icon = item.icon || Sparkles;
                          return (
                            <button
                                key={item.id}
                                onClick={() => handleInspirationClick(item)}
                                disabled={thinking}
                                className="flex items-start gap-4 p-5 bg-white/80 backdrop-blur-sm border border-violet-200/70 rounded-2xl shadow-sm hover:shadow-md hover:border-violet-300 hover:bg-gradient-to-br hover:from-white hover:to-violet-50/60 transition-all duration-300 text-left group hover:-translate-y-1 min-h-[84px] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                            >
                                <span className="w-9 h-9 rounded-xl bg-violet-100 text-violet-600 group-hover:bg-violet-600 group-hover:text-white flex items-center justify-center transition-all duration-200 flex-shrink-0">
                                  <Icon size={18} strokeWidth={2.1} />
                                </span>
                                <span className="min-w-0">
                                  <span className="block text-sm md:text-[15px] font-semibold text-slate-700 group-hover:text-violet-700">{item.label}</span>
                                  {item.description && (
                                    <span className="block mt-0.5 text-xs text-slate-500 leading-relaxed">{item.description}</span>
                                  )}
                                </span>
                            </button>
                          );
                        })}
                      </div>
                  </div>
                )}
                {requiresModelSelection && (
                  <div className="max-w-3xl mx-auto pt-4 space-y-4">
                    <div className="rounded-2xl border border-slate-200 bg-white/95 px-5 py-4 text-sm text-slate-600 shadow-sm">
                      <p className="font-semibold text-slate-700 mb-1">เริ่มใช้งานใน 1 ขั้นตอน</p>
                      <p>เลือกโมเดลก่อนเริ่มแชท แล้วระบบจะแสดงช่องพิมพ์ให้อัตโนมัติ</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                      {models
                        .map((m) => {
                          const active =
                            isModelConfirmed && selectedModel?.version === m.version;
                          const isFreeModel = m.provider === "vllm";
                          return (
                            <button
                              key={m.version}
                              onClick={() => {
                                setSelectedModel(m);
                                setIsModelConfirmed(true);
                              }}
                              className={`w-full flex items-start gap-3 px-4 py-4 rounded-2xl border text-left transition-all duration-300 min-h-[96px] ${
                                active
                                  ? "bg-gradient-to-br from-blue-50/80 to-violet-50/80 border-blue-300 shadow-md shadow-blue-500/10 ring-1 ring-blue-500/20"
                                  : isFreeModel
                                    ? "bg-emerald-50/70 backdrop-blur-sm border-emerald-200/70 hover:border-emerald-300 hover:bg-emerald-50/90 hover:shadow-sm hover:-translate-y-0.5"
                                    : "bg-white/60 backdrop-blur-sm border-slate-200/60 hover:border-blue-300/50 hover:bg-white hover:shadow-sm hover:-translate-y-0.5"
                              }`}
                            >
                              <img src={m.icon} alt={m.name} className="w-5 h-5 object-contain flex-shrink-0 mt-0.5" />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold text-slate-700 leading-tight break-all">
                                  {formatModelTitle(
                                    m.provider === "vllm"
                                      ? toFreeOssDisplayVersion(m.version)
                                      : m.version
                                  )}
                                </div>
                                <div className="text-[11px] text-slate-500 leading-relaxed mt-1">
                                  {m.name} - {m.description || "โมเดลแนะนำ"}
                                </div>
                              </div>
                              {active && (
                                <span className="ml-2 text-[10px] font-semibold text-blue-600 whitespace-nowrap">เลือกแล้ว</span>
                              )}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
                </div>
              ) : (
                <div
                  className={`w-full mx-auto py-6 pb-10 space-y-6 ${
                    shouldShowCanvasPane
                      ? "max-w-none px-4 lg:px-5"
                      : "max-w-4xl px-4 md:px-0"
                  }`}
                >
                  {messages.map((m, i) => (
                    <ChatBubble
                      key={i}
                      m={m}
                      hideCanvasCodeBlocks={Boolean(m.canvasCode) || (isAwaitingAssistant && i === messages.length - 1 && (composerTool === COMPOSER_TOOL_CANVAS || hasEffectiveCanvas))}
                      canvasPaneOpen={shouldShowCanvasPane}
                      onOpenCanvas={reopenCanvasPane}
                      onCloseCanvas={dismissCanvasPane}
                    />
                  ))}
              {isAwaitingAssistant && (
                <div className="flex w-full gap-3 justify-start animate-slideUp mb-4">
                    <div className="flex-shrink-0 w-14 h-14 rounded-full border border-slate-200 bg-white/95 flex items-center justify-center shadow-sm shadow-slate-200 mt-1 overflow-hidden">
                        <video
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="metadata"
                            className="w-full h-full object-contain bg-white"
                            aria-label="AI avatar"
                        >
                            <source src={`${process.env.PUBLIC_URL}/avatar2.mp4`} type="video/mp4" />
                        </video>
                    </div>
                    <div className="relative max-w-[85%] sm:max-w-[75%] md:max-w-[65%] flex flex-col items-start">
                      {!ocrProgress.active && (
                        <div className="px-5 py-3.5 rounded-2xl rounded-tl-sm bg-white border border-slate-100 text-slate-600 shadow-slate-200/50 shadow-sm flex items-start gap-2.5">
                          <Loader2 className="w-4 h-4 text-blue-500 animate-spin mt-0.5" />
                          <div className="space-y-1">
                            <div className="text-sm font-medium animate-pulse">
                              {llmStatusText || "กำลังประมวลผลคำขอ AI..."}
                            </div>
                            <div className="text-[11px] text-slate-500">
                              {llmWaitHint}
                            </div>
                          </div>
                        </div>
                      )}
                      {ocrProgress.active && (
                        <div className="mt-2 w-[340px] rounded-2xl border border-slate-200/60 bg-white shadow-sm overflow-hidden animate-slideUp">
                          {/* Header area */}
                          <div className="bg-slate-50/50 px-3.5 py-2.5 border-b border-slate-100">
                            <div className="flex items-center justify-between text-[11px] font-medium text-slate-700">
                              <div className="flex items-center gap-1.5">
                                {ocrProgress.phase === "analyzing" ? (
                                  <BrainCircuit className="w-3.5 h-3.5 text-indigo-500 animate-pulse" />
                                ) : (
                                  <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" />
                                )}
                                <span className="truncate max-w-[200px]">
                                  {ocrStatusText || "กำลังประมวลผลเอกสาร"}
                                </span>
                              </div>
                              <span className="font-semibold text-blue-600">{computedOcrPercent}%</span>
                            </div>
                          </div>

                          {/* Content area */}
                          <div className="p-2 space-y-2">
                            <div className="flex items-center justify-between px-1 text-[10px] text-slate-500">
                              <span className="font-medium">รายละเอียดการอ่านเอกสาร</span>
                              <button
                                type="button"
                                onClick={() => setIsOcrDetailsExpanded((prev) => !prev)}
                                className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition"
                                title={isOcrDetailsExpanded ? "ย่อรายละเอียด" : "แสดงรายละเอียด"}
                                aria-label={isOcrDetailsExpanded ? "ย่อรายละเอียด" : "แสดงรายละเอียด"}
                              >
                                <span>{isOcrDetailsExpanded ? "ย่อ" : "ขยาย"}</span>
                                {isOcrDetailsExpanded ? (
                                  <ChevronUp className="w-3.5 h-3.5" />
                                ) : (
                                  <ChevronDown className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>
                            {ocrProgress.phase === "analyzing" ? (
                              <div className="flex items-center justify-center gap-2 px-3 py-4 text-xs font-medium text-indigo-600 bg-indigo-50/30 rounded-xl border border-indigo-100/50">
                                <Sparkles className="w-4 h-4" />
                                <span>{llmStatusText || "กำลังประมวลผลคำตอบจาก AI LLM..."}</span>
                              </div>
                            ) : !isOcrDetailsExpanded ? (
                              <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2 text-[10px] text-slate-500">
                                ซ่อนรายละเอียดรายหน้าไว้แล้ว กด "ขยาย" เพื่อดูสถานะแต่ละหน้า
                              </div>
                            ) : (
                              <div className="space-y-1.5 max-h-[180px] overflow-y-auto no-scrollbar">
                                {attachments
                                  .filter((a) => a.state === "processing" || a.state === "done")
                                  .slice(0, 3)
                                  .map((a) => {
                                    const pageState = ocrPageStateByFile[a.id] || { totalPages: 0, states: [], pageTokens: [] };
                                    const states = Array.isArray(pageState.states) ? pageState.states : [];
                                    const pageTokens = Array.isArray(pageState.pageTokens) ? pageState.pageTokens : [];
                                    const total = pageState.totalPages || states.length || a.progress?.totalPages || 0;
                                    if (total <= 0) return null;
                                    
                                    return (
                                      <div key={a.id} className="rounded-xl border border-slate-100 bg-white px-2.5 py-2">
                                        <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-700 mb-1.5">
                                          <FileText className="w-3.5 h-3.5 text-blue-500" />
                                          <span className="truncate max-w-[220px]">{a.name}</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-1">
                                          {Array.from({ length: total }, (_, idx) => {
                                            const st = states[idx] || "pending";
                                            const isDone = st === "done";
                                            const isProc = st === "processing";
                                            const isErr = st === "error";
                                            
                                            let StatusIcon = Loader2;
                                            if (isDone) StatusIcon = CheckCircle2;
                                            else if (isErr) StatusIcon = XCircle;
                                            else if (st === "pending") StatusIcon = undefined;
                                            
                                            return (
                                              <div 
                                                key={`${a.id}-p-${idx}`} 
                                                className={`flex items-center justify-between px-1.5 py-1 rounded bg-slate-50/80 text-[9px] border border-slate-100/50 ${isProc ? 'ring-1 ring-blue-500/20' : ''}`}
                                              >
                                                <div className="flex items-center gap-1">
                                                  {StatusIcon ? (
                                                    <StatusIcon className={`w-3 h-3 ${isDone ? 'text-emerald-500' : isErr ? 'text-rose-500' : 'text-blue-500 animate-spin'}`} />
                                                  ) : (
                                                    <div className="w-3 h-3 rounded-full border-[1.5px] border-slate-200" />
                                                  )}
                                                  <span className={isDone ? 'text-slate-600' : isProc ? 'text-blue-600 font-medium' : 'text-slate-400'}>
                                                    หน้า {idx + 1}
                                                  </span>
                                                </div>
                                                {Number(pageTokens[idx] || 0) > 0 && (
                                                  <span className="text-slate-400 font-medium">
                                                    {Number(pageTokens[idx] || 0)}<span className="text-[8px] ml-[1px]">t</span>
                                                  </span>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                </div>
              )}
              {thinking && !isAwaitingAssistant && composerTool === COMPOSER_TOOL_CANVAS && !isStreamingCanvas && (
                <div className="flex items-center gap-2 text-xs text-violet-600 pl-2 animate-pulse">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>กำลังสร้างชิ้นงาน… (งานขนาดใหญ่อาจใช้เวลาสักครู่)</span>
                </div>
              )}
              <div ref={chatEndRef} />
                </div>
              )}
            </div>

            <div className="relative z-20">
              <div
                className={`mx-auto w-full px-4 md:px-0 pb-6 ${
                  shouldShowCanvasPane ? "max-w-none lg:px-5" : "max-w-3xl"
                }`}
              >
                {!requiresModelSelection && (
                  <ChatInputBar
                    input={input}
                    setInput={setInput}
                    sendMessage={sendMessage}
                    onStop={stopMessage}
                    responseMode={responseMode}
                    setResponseMode={setResponseMode}
                    thinking={thinking}
                    showStatusText={false}
                    attachments={attachments}
                    onPickFiles={handlePickFiles}
                    onRemoveAttachment={handleRemoveAttachment}
                    onCanvasAction={handleCanvasAction}
                    activeTool={inputActiveTool}
                    onClearTool={clearComposerTool}
                    ocrStatusText={ocrStatusText}
                    activeModelLabel={activeModelLabel}
                    placeholderText={
                      composerTool === COMPOSER_TOOL_CANVAS
                        ? messages.length === 0
                          ? "บอกสิ่งที่อยากสร้าง เช่น 'เว็บขายกาแฟ' หรือเลือกเทมเพลตด้านบน"
                          : "บอกสิ่งที่อยากสร้าง เดี๋ยวผมถามรายละเอียดที่จำเป็นให้"
                        : ""
                    }
                  />
                )}
                <div className="text-center mt-2">
                  <p className="text-[10px] text-slate-400">
                    AI อาจแสดงข้อมูลที่คลาดเคลื่อน โปรดตรวจสอบความถูกต้อง
                  </p>
                </div>
              </div>
            </div>
          </section>

          {shouldShowCanvasPane && (
            <aside className="hidden lg:flex flex-1 min-w-0 bg-[#f9fafb] p-4 flex-col">
              <div className="flex-1 flex flex-col bg-white border border-slate-200/80 rounded-2xl shadow-sm overflow-hidden">
                <div className="h-14 px-4 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="text-sm font-semibold text-slate-700 truncate">
                      ตัวอย่าง HTML CODE
                    </div>
                    {isStreamingCanvas && (
                      <div className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 shrink-0">
                        <span className="relative flex h-2 w-2">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75"></span>
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500"></span>
                        </span>
                        <span className="text-[11px] font-semibold text-blue-700 whitespace-nowrap">
                          กำลังเขียนโค้ด…
                        </span>
                      </div>
                    )}
                    {/* Security Guard badge — runs in an isolated sandbox */}
                    <div
                      className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 shrink-0"
                      title="โค้ดถูกรันในแซนด์บ็อกซ์ที่แยกออกจากระบบหลัก เพื่อความปลอดภัย"
                    >
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
                      </span>
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                      <span className="text-[11px] font-semibold text-emerald-700 whitespace-nowrap">
                        Security Guard
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="inline-flex items-center rounded-full bg-slate-100 p-0.5">
                      <button
                        type="button"
                        onClick={() => setCanvasViewMode("code")}
                        className={`px-3 py-1 text-[11px] font-medium rounded-full transition-all ${
                          canvasViewMode === "code"
                            ? "bg-white text-slate-800 shadow-sm"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                      >
                        Code
                      </button>
                      <button
                        type="button"
                        onClick={() => setCanvasViewMode("preview")}
                        className={`px-3 py-1 text-[11px] font-medium rounded-full transition-all ${
                          canvasViewMode === "preview"
                            ? "bg-white text-slate-800 shadow-sm"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                      >
                        Preview
                      </button>
                    </div>

                    <div className="flex items-center gap-3 text-slate-400">
                      <button
                        type="button"
                        onClick={handleCanvasDownload}
                        disabled={!canvasArtifactCode}
                        className="inline-flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        aria-label="ดาวน์โหลดไฟล์"
                        title="ดาวน์โหลดไฟล์"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <div className="w-[1px] h-4 bg-slate-200 mx-1"></div>
                      <button
                        type="button"
                        onClick={dismissCanvasPane}
                        className="inline-flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
                        aria-label="ปิด Canvas"
                        title="ปิด Canvas"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex-1 min-h-0 relative">
                  {isStreamingCanvas ? (
                    /* ── Live code streaming (Gemini-style "writing code" view) ── */
                    <CanvasCodeView
                      code={liveCanvasCode || " "}
                      language="html"
                      streaming
                    />
                  ) : canvasArtifactCode ? (
                    canvasViewMode === "preview" ? (
                      isReactCanvasArtifact ? (
                        /* ── React canvas: Sandpack with editor + preview ── */
                        <div className="h-full overflow-hidden bg-white">
                          <Sandpack
                            template="react"
                            files={{ "/App.js": canvasArtifactCode }}
                            options={{
                              showNavigator: false,
                              showTabs: false,
                              showLineNumbers: true,
                              editorHeight: "100%",
                              editorWidthPercentage: 45,
                              autorun: true,
                            }}
                            customSetup={{
                              dependencies: { "lucide-react": "latest" },
                            }}
                          />
                        </div>
                      ) : (
                        /* ── HTML canvas: blob URL iframe ──
                             Blob URL gives isolated origin (blob:<uuid>).
                             allow-same-origin is safe here — the blob origin
                             is NOT the parent app's origin, so localStorage
                             is isolated. External CDNs load normally.
                             Smart link handler: anchor links scroll in-page,
                             external links open in new tab. */
                        <div className="h-full bg-white overflow-hidden relative">
                          <iframe
                            title="canvas-preview-right-pane"
                            src={canvasBlobUrl || undefined}
                            className="w-full h-full bg-white border-0"
                            sandbox={CANVAS_SANDBOX_POLICY}
                            onLoad={handleCanvasIframeLoad}
                          />
                          {canvasLoading && (
                            <div className="absolute inset-0 z-10 bg-white flex flex-col">
                              {/* Indeterminate top progress bar */}
                              <div className="h-1 w-full bg-slate-100 overflow-hidden">
                                <div className="h-full w-1/3 bg-gradient-to-r from-blue-500 via-violet-500 to-cyan-400 rounded-full animate-canvasLoadingBar" />
                              </div>
                              {/* Centered loading state */}
                              <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
                                <div className="w-14 h-14 rounded-2xl border border-slate-200 bg-white shadow-sm flex items-center justify-center text-slate-700 animate-pulse">
                                  <Code2 className="w-7 h-7" strokeWidth={2} />
                                </div>
                                <div className="mt-5 text-[15px] font-medium text-slate-700">
                                  Loading your app...
                                </div>
                                <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-400">
                                  <Clock className="w-3.5 h-3.5" />
                                  Ready soon
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    ) : (
                      <CanvasCodeView
                        code={canvasArtifactCode}
                        language={canvasArtifactKind === "react" ? "jsx" : "html"}
                      />
                    )
                  ) : (
                    <div className="h-full bg-white flex flex-col items-center justify-center px-8 text-center">
                      <div className="text-sm font-medium text-slate-700">
                        {isCanvasRendering
                          ? "กำลังเขียนโค้ด..."
                          : "พร้อมแสดงผล Canvas"}
                      </div>
                      <div className="mt-2 text-xs text-slate-500">
                        {isCanvasRendering
                          ? "กำลังสร้าง HTML และเตรียมแสดงผลในพาเนลขวา"
                          : "ส่งคำสั่งใน Canvas mode เพื่อดูผลทางขวามือ"}
                      </div>
                      <div className="mt-2 text-[11px] text-slate-400">
                        รองรับ ` ```html ` และ ` ```jsx `
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </aside>
          )}
        </div>
      </main>
      <ConfirmDeleteModal
        open={!!deleteTarget}
        chatTitle={deleteTarget?.title_short || "this chat"}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
      {quotaNoticeModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <button
            className="absolute inset-0 bg-slate-900/35 backdrop-blur-[1px]"
            onClick={() => setQuotaNoticeModal(null)}
            aria-label="Close quota notice modal"
          />
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_80px_rgba(15,23,42,0.25)]">
            <div className="px-5 pt-5 pb-4 bg-gradient-to-r from-blue-50 via-indigo-50 to-cyan-50 border-b border-slate-100">
              <div className="flex items-start gap-3">
                <div
                  className={`mt-0.5 h-10 w-10 rounded-xl flex items-center justify-center ${quotaNoticeModal.type === "switched"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-amber-100 text-amber-700"
                    }`}
                >
                  {quotaNoticeModal.type === "switched" ? (
                    <Sparkles size={18} />
                  ) : (
                    <AlertTriangle size={18} />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-base font-semibold text-slate-800">
                    {quotaNoticeModal.type === "switched"
                      ? "ระบบสลับเป็นโหมดใช้งานต่อเนื่องแล้ว"
                      : "โควต้าใกล้เต็ม ระบบอาจสลับอัตโนมัติ"}
                  </div>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                    {quotaNoticeModal.type === "switched"
                      ? "โควต้าของโมเดลขั้นสูงหมดแล้วในตอนนี้ ระบบจึงเปลี่ยนเป็นโมเดลฟรีชั่วคราว เพื่อให้คุณคุยต่อได้ทันที"
                      : `คุณใช้โควต้าโมเดลขั้นสูงไปแล้วประมาณ ${Number(
                        quotaNoticeModal.percent || 0
                      ).toFixed(1)}% หากเต็ม ระบบจะสลับไปโมเดลฟรีให้อัตโนมัติ`}
                  </p>
                </div>
              </div>
            </div>
            {quotaNoticeModal.type === "switched" && (
              <div className="mx-5 mt-4 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                จาก: {quotaNoticeModal.fromModelName} → ตอนนี้ใช้: {quotaNoticeModal.toModelName}
              </div>
            )}
            <div className="px-5 py-4 flex justify-end gap-2">
              <button
                onClick={() => {
                  setActiveMenu("usage");
                  setQuotaNoticeModal(null);
                }}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm"
              >
                ดูการใช้งานวันนี้
              </button>
              <button
                onClick={() => setQuotaNoticeModal(null)}
                className="px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm"
              >
                {quotaNoticeModal.type === "switched" ? "ใช้งานต่อ" : "รับทราบ"}
              </button>
            </div>
          </div>
        </div>
      )}

      {sheetModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <button
            className="absolute inset-0 bg-slate-900/35 backdrop-blur-[1px]"
            onClick={() => {
              if (!sheetLoading) setSheetModalOpen(false);
            }}
            aria-label="Close Google Sheet modal"
          />
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_80px_rgba(15,23,42,0.25)]">
            <div className="px-5 pt-5 pb-4 bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 border-b border-slate-100">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 h-10 w-10 rounded-xl flex items-center justify-center bg-emerald-100 text-emerald-700">
                  <BarChart3 size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-base font-semibold text-slate-800">
                    สร้าง Dashboard จาก Google Sheet
                  </div>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                    วางลิงก์ Google Sheet ที่เปิดสิทธิ์ให้อ่านได้ ระบบจะดึงข้อมูลมาให้ AI ออกแบบแดชบอร์ด
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 py-4 space-y-3">
              <input
                type="url"
                value={sheetUrl}
                onChange={(e) => {
                  setSheetUrl(e.target.value);
                  if (sheetError) setSheetError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !sheetLoading) submitSheetUrl();
                }}
                disabled={sheetLoading}
                placeholder="https://docs.google.com/spreadsheets/d/.../edit"
                autoFocus
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none disabled:bg-slate-50"
              />
              {sheetError && (
                <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700 leading-relaxed">
                  {sheetError}
                </div>
              )}
              <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-500 leading-relaxed">
                ต้องตั้งค่า Sheet ให้เปิดอ่านได้: <b>แชร์ → ทุกคนที่มีลิงก์ = ผู้อ่าน</b> หรือ <b>ไฟล์ → แชร์ → เผยแพร่ทางเว็บ</b>
              </div>
            </div>
            <div className="px-5 py-4 flex justify-end gap-2 border-t border-slate-100">
              <button
                onClick={() => setSheetModalOpen(false)}
                disabled={sheetLoading}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm disabled:opacity-50"
              >
                ยกเลิก
              </button>
              <button
                onClick={submitSheetUrl}
                disabled={sheetLoading}
                className="px-4 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm disabled:opacity-60 inline-flex items-center gap-2"
              >
                {sheetLoading ? (
                  <>
                    <span className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                    กำลังดึงข้อมูล…
                  </>
                ) : (
                  "ดึงข้อมูล & สร้าง"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
